/**
 * The pure state transitions (web-plan.md F1) — every rule from the desktop's
 * `state/*.rs`, written as functions over mutable drafts (the runtime calls
 * them inside Immer's `produce`). Each carries its inherited rule in a
 * comment; none of them does IO.
 */
import type {
  Attachment,
  Call,
  CallLog,
  ChannelCard,
  ChannelState,
  CommentCount,
  ViewCount,
  DirectoryEntry,
  Entity,
  Expiring,
  ForwardChannel,
  Forwarded,
  FriendsSnapshot,
  Message,
  MessageRead,
  MyReaction,
  PresenceEntry,
  ReactionGroup,
  ReplyQuote,
  ReplyTo,
  SeqReactions,
  Service,
  UserStatus,
} from "../gen/dto";
import { SERVICE_KINDS } from "../gen/dto";

import {
  type Ability,
  type Standing,
  COMMENT_KIND_WORDS,
  commentDeletableForEverybodyBy,
  postDeletableBy,
  postEditableBy,
  rightsBits,
  standingOf,
} from "../lib/broadcast";
import { deviceLine } from "../lib/device";
import type { Dict } from "../lib/i18n";
import { drawable } from "../lib/stickers";
import type { AppLink } from "../lib/links";
import {
  type Preview,
  type PreviewThumb,
  attachmentPreview,
  cutPreview,
  markedPreview,
  plainPreview,
  prefixUnits,
  spoilersWithin,
  withThumb,
} from "../lib/preview";
import { want as wantPerson } from "./people";
import {
  type Bubble,
  type Byline,
  type ChannelCardEntry,
  type ChannelLinkView,
  type ChannelVoice,
  type ClockState,
  type CommandsEntry,
  type DirectoryState,
  type FeedState,
  type ForwardDraft,
  type FriendsState,
  type Person,
  type PresenceState,
  type ReadsEntry,
  type Room,
  type SecurityNotice,
  type SidebarTab,
  type UiState,
  CLOCK_SAMPLES,
  DELETE_WINDOW_MS,
  EDIT_WINDOW_MS,
  MAX_FORWARD,
  MAX_SELECTED,
  NOTICE_KINDS,
  PENDING_SEQ,
  TYPING_EXPIRY_MS,
  TYPING_SEND_PERIOD_MS,
  type VoiceCall,
  type VoicePeer,
  WINDOW_SIZE,
  channelCardKey,
  handleCardKey,
  isBroadcastRoom,
  isDmRoom,
  isFormerDm,
  isOfficial,
  isPet,
} from "./types";

// ---------------------------------------------------------------------------
// Directory

/** The account's row, noting that it is wanted when it is not here
 * (docs/fast-start.md D1057): the door to the directory for whatever draws a face,
 * a handle or a mark with no `nameOf` beside it. The row itself, not a guess:
 * "no row yet" and "a row with nothing filled in" draw alike but mean different
 * things to a caller. */
export function met(dir: DirectoryState, userId: number): Person | undefined {
  const p = dir.people[userId];
  if (p === undefined) wantPerson(userId);
  return p;
}

/** The display-name ladder (D59): display_name → username → `#{id}`. One
 * function so every name-drawing site agrees, and every one of them asks about
 * an account nobody has named yet (D1057). */
export function nameOf(dir: DirectoryState, userId: number): string {
  const p = met(dir, userId);
  if (!p) return `#${userId}`;
  return p.displayName && p.displayName !== "" ? p.displayName : p.username;
}

export function selfIdOf(dir: DirectoryState): number {
  return dir.selfId !== 0 ? dir.selfId : dir.selfIdFallback;
}

/** Id 0 is never anybody — the guard every call site repeats. */
export function isSelf(dir: DirectoryState, userId: number): boolean {
  const self = selfIdOf(dir);
  return self !== 0 && userId === self;
}

/** Everybody met, by id and handle — what a send finds `@username`s by
 * (dioxus-plan.md D284), after asking the gateway about any handle in the words
 * that is not here (docs/fast-start.md D1057). The whole directory, not the open
 * channel's roster the composer's `@` list offers: a mention is a link to a
 * person, a hand-typed `@olya` means Olya whether or not the list was open, and
 * whether she can read the channel is its privacy's business (D114). */
export function handlesOf(dir: DirectoryState): [number, string][] {
  return Object.entries(dir.people).map(([id, p]) => [Number(id), p.username]);
}

/** Whether two spellings name one account: a username is one account whatever
 * its case (gateway/migrations/0035_username_fold.sql), and the spaces around
 * a typed one are not part of it. */
function sameUsername(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** A directory row out of its wire entry — the keys only a pet carries
 * (pets-plan.md D349; the official pet's `official`, official-pet-plan.md
 * D939) included, and absent when the entry does not say them. */
function personOf(e: DirectoryEntry): Person {
  const p: Person = {
    username: e.username,
    displayName: e.display_name,
    bio: e.bio,
    avatarFileId: e.avatar_file_id,
    createdAt: e.created_at,
    bubbleLight: e.bubble_light,
    bubbleDark: e.bubble_dark,
  };
  if (e.kind !== undefined) p.kind = e.kind;
  if (e.pet_rev !== undefined) p.petRev = e.pet_rev;
  if (e.retired === true) p.retired = true;
  if (e.official === true) p.official = true;
  return p;
}

/** Whether a pet's card moved between two readings of its row: its revision,
 * or its retirement (D365), or its arrival — what a chat's `/` list read
 * before it is stale against (D360). */
function petMoved(before: Person | undefined, after: Person): boolean {
  if (!isPet(after)) return false;
  return (
    !isPet(before) ||
    before?.petRev !== after.petRev ||
    (before?.retired === true) !== (after.retired === true)
  );
}

/** A pet whose card moved: the revision it moved to, and whether it is
 * retired now (D365) — which takes it out of every `/` list whatever its
 * revision says. */
export interface PetMove {
  petId: number;
  rev: number;
  retired: boolean;
}

function moveOf(petId: number, pet: Person): PetMove {
  return { petId, rev: pet.petRev ?? 0, retired: pet.retired === true };
}

/** Whether a row read again says what the row held says. */
function samePerson(a: Person | undefined, b: Person): boolean {
  return (
    a !== undefined &&
    a.username === b.username &&
    a.displayName === b.displayName &&
    a.bio === b.bio &&
    a.avatarFileId === b.avatarFileId &&
    a.createdAt === b.createdAt &&
    a.bubbleLight === b.bubbleLight &&
    a.bubbleDark === b.bubbleDark &&
    a.kind === b.kind &&
    a.petRev === b.petRev &&
    a.retired === b.retired &&
    a.official === b.official
  );
}

/** Whether the directory holds these rows as they are: an answer that says nothing
 * new has nothing to publish. */
export function holdsAll(dir: DirectoryState, entries: readonly DirectoryEntry[]): boolean {
  return entries.every((e) => samePerson(dir.people[e.user_id], personOf(e)));
}

/** The rows an answer carried (docs/fast-start.md D1056), merged: they are
 * whoever that answer names, and everybody else stays met. A row that says what
 * it said before is left alone, because answers repeat the same people constantly
 * and a new object would read to every subscriber as a changed directory. Answers
 * the pets whose card moved since the last reading, for the `/` lists read against
 * them (D360). */
export function applyDirectory(
  dir: DirectoryState,
  entries: readonly DirectoryEntry[],
  selfName: string,
): PetMove[] {
  const moved: PetMove[] = [];
  for (const e of entries) {
    const next = personOf(e);
    const held = dir.people[e.user_id];
    if (petMoved(held, next)) moved.push(moveOf(e.user_id, next));
    if (!samePerson(held, next)) dir.people[e.user_id] = next;
    // Login swallows a failing /v1/me; the username match is the fallback
    // identity until the real one lands. Blind to case, the way the gateway
    // matches names: `selfName` is what was typed at the sign-in card, and
    // `аня` signs in to the account called `Аня`.
    if (dir.selfId === 0 && selfName !== "" && sameUsername(e.username, selfName)) {
      dir.selfIdFallback = e.user_id;
    }
  }
  return moved;
}

/** A whole DirectoryEntry, applied whole — a pet's `kind`, `pet_rev` and
 * `retired` with it, or the next frame about a pet would turn it back into a
 * person. An account this client has not met stays unmet (docs/fast-start.md
 * D1056, amending D462 and D361's frame): the frame reaches every socket, and
 * search asks the gateway now (D1058). created_at 0 in an update is ignored (a
 * rename must not repaint the avatar). Answers the pet's move when its card
 * moved. */
export function applyDirectoryUpdate(dir: DirectoryState, e: DirectoryEntry): PetMove | null {
  const p = dir.people[e.user_id];
  if (!p) return null;
  const next = personOf(e);
  const moved = petMoved(p, next) ? moveOf(e.user_id, next) : null;
  p.username = e.username;
  p.displayName = e.display_name;
  p.bio = e.bio;
  p.avatarFileId = e.avatar_file_id;
  if (e.created_at !== 0) p.createdAt = e.created_at;
  p.bubbleLight = e.bubble_light;
  p.bubbleDark = e.bubble_dark;
  // Written only when they differ: a person's row has none of the three, and
  // writing an absent key as `undefined` would still read to Immer as a change.
  if (p.kind !== next.kind) p.kind = next.kind;
  if (p.petRev !== next.petRev) p.petRev = next.petRev;
  if (p.retired !== next.retired) p.retired = next.retired;
  // The mark with them (official-pet-plan.md D939): an update rebuilt without
  // it would lose the mark.
  if (p.official !== next.official) p.official = next.official;
  return moved;
}

/** The stand's own pet (official-pet-plan.md D934), as far as the directory
 * knows it: its id, or null while no row says so. */
export function officialPetOf(dir: DirectoryState): number | null {
  for (const [id, p] of Object.entries(dir.people)) {
    if (isOfficial(p)) return Number(id);
  }
  return null;
}

/** Whether a row is the dialog with the official pet (D936) — no wire flag
 * says so; the peer's `official` does (D947). */
export function isOfficialRoom(room: Room, dir: DirectoryState): boolean {
  return isDmRoom(room) && room.peerId !== null && isOfficial(dir.people[room.peerId]);
}

/** The dialog with the official pet, when the list holds it. */
export function officialRoomOf(rooms: readonly Room[], dir: DirectoryState): Room | undefined {
  return rooms.find((r) => isOfficialRoom(r, dir));
}

/** Whether a security card still asks for an answer (D943): it raises the
 * official row while it does. An ended one has had its answer. */
export function noticeWaiting(n: SecurityNotice): boolean {
  return n.state !== "ended";
}

/** Whether this build can word a notice's kind (D940): an unknown one says
 * to update, as D477 has every unknown thing say. */
export function noticeKnown(n: SecurityNotice): boolean {
  return (NOTICE_KINDS as readonly string[]).includes(n.kind);
}

/** The device a sign-in notice names, as the sessions list draws its line
 * (D942). */
export function noticeDevice(n: SecurityNotice, t: Dict): string {
  return deviceLine(n.signin ?? {}, t);
}

/** What the official row says while a card waits (D943) — the newest
 * waiting card's, with its time — or null when none does. */
export function noticeRowLine(
  notices: readonly SecurityNotice[],
  nowMs: number,
  t: Dict,
): { line: string; time: string } | null {
  const card = notices.find(noticeWaiting);
  if (!card) return null;
  return {
    line: noticeKnown(card) ? t.noticeRow(noticeDevice(card, t)) : t.noticeUnknown(),
    time: noticeStamp(card.atMs, nowMs, t),
  };
}

/** When a notice came: «только что» for the last minute, then as the list
 * stamps a message (D942). */
export function noticeStamp(atMs: number, nowMs: number, t: Dict): string {
  return nowMs - atMs < 60_000 ? t.noticeJustNow() : sidebarStamp(atMs, nowMs, t);
}

/** The official row stands where its newest waiting card puts it (D943),
 * every other row where its messages do: `noticeAtMs` written from the cards,
 * and the list sorted again when that moved anything. */
export function raiseForNotices(
  rooms: Room[],
  dir: DirectoryState,
  notices: readonly SecurityNotice[],
): void {
  const newest = notices.filter(noticeWaiting).reduce((at, n) => Math.max(at, n.atMs), 0);
  let moved = false;
  for (const row of rooms) {
    const at = newest > 0 && isOfficialRoom(row, dir) ? newest : 0;
    if (row.noticeAtMs !== at) {
      row.noticeAtMs = at;
      moved = true;
    }
  }
  if (moved) resort(rooms);
}

/** The pet a direct conversation is with, or null — for a channel, a DM
 * with a person, or a peer the directory has not heard of yet. */
export function petPeerOf(room: Room, dir: DirectoryState): number | null {
  if (!isDmRoom(room) || room.peerId === null) return null;
  return isPet(met(dir, room.peerId)) ? room.peerId : null;
}

/** Whether a row belongs on a tab (D179, pets-plan.md D361): Friends holds the
 * conversations with people, Pets the ones with pets, Channels everything that
 * is not a conversation, and All — and any key this build does not know —
 * everything. A DM whose peer the directory has not heard of yet is a
 * person's: that is what it was before pets, and what a pet's row turns into
 * only when the directory says so. A former friend's conversation
 * (friends-plan.md D411) is on All alone: not a friend, not a channel. */
export function tabHolds(tab: SidebarTab, room: Room, dir: DirectoryState): boolean {
  // A broadcast channel read without a subscription is on no list (D837).
  if (room.guest) return false;
  switch (tab) {
    // Broadcast channels have a tab of their own (docs/broadcast-channels-plan.md
    // D863), and the groups' tab is groups.
    case "channels":
      return !isDmRoom(room) && !isBroadcastRoom(room);
    case "broadcasts":
      return isBroadcastRoom(room);
    case "friends":
      return isDmRoom(room) && room.member && petPeerOf(room, dir) === null;
    case "pets":
      return petPeerOf(room, dir) !== null;
    default:
      return true;
  }
}

/** The chats whose `/` list a pet's move makes stale (D360): each list that
 * holds the pet's commands at an older revision — and each that holds none of
 * them, because a pet with no commands has no row to carry its revision and
 * may be in that chat all the same. A list that already has the pet at this
 * revision, or past it, stays — unless the pet was retired, which takes its
 * rows out of every list (D365) and puts none anywhere. A list still in the
 * air is asked again: its answer may be older than the move. */
export function staleCommandLists(
  commands: Record<number, CommandsEntry>,
  move: PetMove,
): number[] {
  const stale: number[] = [];
  for (const [key, entry] of Object.entries(commands)) {
    if (entry.t !== "ready") {
      stale.push(Number(key));
      continue;
    }
    const rows = entry.commands.filter((c) => c.pet_id === move.petId);
    const moved = move.retired
      ? rows.length > 0
      : rows.length === 0 || rows.some((c) => c.pet_rev < move.rev);
    if (moved) stale.push(Number(key));
  }
  return stale;
}

/** Somebody other than the reader has read up to `seq` (the `seen` frame,
 * chat-plan.md D422): the checks on the reader's own messages turn (D423).
 * Answers whether the row moved — an older place moves nothing. */
export function roomsOnSeen(rooms: Room[], channelId: number, seq: number): boolean {
  const row = roomOf(rooms, channelId);
  if (!row || seq <= row.seenSeq) return false;
  row.seenSeq = seq;
  return true;
}

/** Stop and `/restart` (D368), from the frame or the send that took it back. */
export function roomsOnPetStopped(rooms: Room[], channelId: number, stopped: boolean): void {
  const row = roomOf(rooms, channelId);
  if (row) row.petStopped = stopped;
}

// ---------------------------------------------------------------------------
// Previews and stamps

const PREVIEW_UNITS = 80;
/** How much of the answered message's text a quote carries (D273): the wire's
 * `QUOTE_UNITS` (wire/src/dto.rs), for the quotes this client writes itself —
 * over its own reply while it is in flight, and over the replies to a message
 * whose edit it has just heard of. */
const QUOTE_UNITS = 128;

/** One function for sidebar rows AND reply quotes: service → text (cut at
 * 80 UTF-16 units, never through a surrogate pair, its spoilers still hidden —
 * D343) → attachment kind → "". */
export function preview(m: Message, t: Dict): Preview {
  return summary(m.service ?? null, m.text, m.entities ?? [], m.attachments ?? [], t);
}

/** The same line for the message a reply answers, from the quote the reply
 * carries of it (D273). The quote holds more of a long text than the line
 * shows, so the cut lands where it would have on the whole text — and its
 * spoilers, the one entity it carries (D343), are cut where the text was. */
export function quotePreview(q: ReplyQuote, t: Dict): Preview {
  return summary(q.service ?? null, q.text ?? "", q.entities ?? [], q.attachments ?? [], t);
}

/** The quote of `m` as it now stands (D273) — `ReplyQuote::of` on the Rust
 * side, which is what the gateway sends: who wrote it, the start of its text
 * and the spoilers over that start (D343), what it holds, and what kind of
 * notice it is if the server wrote it. */
export function quoteOf(m: Message): ReplyTo {
  // The channel speaks for a post (docs/broadcast-channels-plan.md D842): the
  // person behind it is never on a quote, whoever reads it.
  const quote: ReplyQuote = { sender_id: m.as_channel === true ? 0 : m.sender_id };
  if (m.as_channel === true) quote.as_channel = true;
  const text = prefixUnits(m.text, QUOTE_UNITS);
  if (text !== "") quote.text = text;
  const spoilers = spoilersWithin(m.entities ?? [], text.length);
  if (spoilers.length) quote.entities = spoilers;
  if (m.attachments?.length) quote.attachments = m.attachments;
  if (m.service) quote.service = m.service;
  return { state: "live", ...quote };
}

function summary(
  service: Service | null,
  text: string,
  entities: Entity[],
  attachments: Attachment[],
  t: Dict,
): Preview {
  if (service) {
    const kind = (service as { kind: string }).kind;
    if (!SERVICE_KINDS.includes(kind as (typeof SERVICE_KINDS)[number])) {
      return plainPreview(t.unsupportedMessage());
    }
    // Whether it can still be joined, and how it ended (D540).
    if (kind === "call") return callLine(service as unknown as CallLog, t);
    // A promo code waiting (official-pet-plan.md D981): the amount is the
    // news, the date is the line's in the feed.
    if (service.kind === "floats_code") return plainPreview(t.floatsCode(service.amount, null));
    // Short, name-free forms: the row is narrow and the directory is not in
    // scope where this is built.
    return plainPreview(t.svcPreview(kind));
  }
  // The first picture, in front of the caption or instead of the icon (D396).
  const thumb = attachments[0] ? thumbOf(attachments[0]) : null;
  // Never split a surrogate pair: `cutPreview` steps back off a low half.
  if (text !== "") return withThumb(cutPreview(text, entities, PREVIEW_UNITS), thumb);
  // The pack's microphone where a surface draws one (D501).
  if (attachments[0]?.kind === "voice") {
    return markedPreview(attachmentSummary(attachments, t), "voice");
  }
  // «😀» — a sticker's own emoji is the line (docs/stickers-plan.md D573),
  // never left out for its poster as a kind's icon is: it is what the sticker
  // says.
  const sticker = attachments[0];
  if (sticker?.kind === "sticker" && drawable(sticker)) {
    return withThumb(plainPreview(t.stickerPreview(sticker.sticker?.emoji ?? "")), thumb);
  }
  return attachmentPreview(attachmentSummary(attachments, t), thumb);
}

/** The line about a call widget (D540): «Идёт звонок» alone while it runs —
 * the row's face already shows the call, so the line has no icon (D542); the
 * hung-up phone and its length once over — «Звонок без
 * ответа» for a ring nobody answered (under a minute alone: calling that a
 * 40-second call would be a lie), «Звонок прерван» for one a gateway restart
 * cut short, whose length nobody measured. The emoji is for a surface that can
 * draw only a string. The desktop's `feed::call_line`. */
function callLine(log: CallLog, t: Dict): Preview {
  if (log.live) return plainPreview(t.previewCallLive());
  const secs = log.duration_s;
  let words: string;
  if (secs == null) words = t.previewCallInterrupted();
  else if (log.end_reason === "alone_timeout" && secs < 60) words = t.previewCallUnanswered();
  else words = t.previewCallLasted(t.durationS(secs));
  return markedPreview(`📞 ${words}`, "callEnded");
}

/** Who wrote `m`, for the row's line (D541) — null for a message the server
 * wrote: a call or a member joining is a fact about the chat. The desktop's
 * `Byline::of`. */
export function bylineOf(m: Message): Byline | null {
  if (m.service) return null;
  // A post is the channel's (docs/broadcast-channels-plan.md D863): the row
  // is its author, and the line names nobody in front of it.
  if (m.as_channel === true) return null;
  const mark = m.forwarded ? "forwarded" : m.reply_to_seq != null ? "reply" : null;
  return { senderId: m.sender_id, mark };
}

/** The picture a chat-list row draws for an attachment (D396): a photograph's
 * thumbnail — the photograph itself, the preview weight, for one sent without
 * it — a film's poster, or a sticker's (docs/stickers-plan.md D549), which a
 * still sticker is itself. A film without a poster has nothing to draw, and
 * keeps its icon; so does a sticker of a kind this build cannot name. The
 * desktop's `feed::thumb_of`. */
function thumbOf(a: Attachment): PreviewThumb | null {
  const round = a.kind === "video_message";
  let fileId: string | null | undefined;
  if (a.kind === "photo") fileId = a.thumb_file_id || a.file_id;
  else if (a.kind === "video" || round) fileId = a.thumb_file_id;
  else if (drawable(a)) fileId = a.thumb_file_id || a.file_id;
  if (!fileId) return null;
  return {
    fileId,
    spoiler: a.spoiler === true,
    round,
    ...(a.kind === "sticker" ? { sticker: true } : {}),
  };
}

function attachmentSummary(attachments: Attachment[], t: Dict): string {
  const first = attachments[0];
  if (first) {
    // An album of photos and videos together is «3 медиа», not the first one's kind.
    if (
      (first.kind === "photo" || first.kind === "video") &&
      attachments.some((a) => a.kind !== first.kind)
    ) {
      return t.mediaPreview("media", attachments.length);
    }
    switch (first.kind) {
      case "photo":
        return t.mediaPreview("photo", attachments.length);
      case "video":
        return t.mediaPreview("video", attachments.length);
      case "video_message":
        return t.mediaPreview("video_message", 1);
      case "voice":
        return t.mediaPreview("voice", 1, first.duration_ms);
      case "file":
        return `📎 ${first.file_name ?? ""}`.trim();
      // A sticker this build can draw is its emoji (`summary`, D573); one of
      // a format it cannot name asks for an update (D552).
      default:
        return t.unsupportedAttachment();
    }
  }
  return "";
}

export function sidebarStamp(atMs: number, nowMs: number, t: Dict): string {
  const at = new Date(atMs);
  const now = new Date(nowMs);
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  if (sameDay) {
    return at.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (
    at.getFullYear() === yesterday.getFullYear() &&
    at.getMonth() === yesterday.getMonth() &&
    at.getDate() === yesterday.getDate()
  ) {
    return t.yesterday();
  }
  return at.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// ---------------------------------------------------------------------------
// Who read a message (chat-plan.md D422, D423)

/** The key of message `seq` of `channelId` in `UiState.reads`. */
export function readsKey(channelId: number, seq: number): string {
  return `${channelId}:${seq}`;
}

/** «Details» opened over message `seq` of `channelId`: answers whether the
 * gateway is to be asked — nothing is known about it, the last ask failed, or
 * what is known may be missing a reader — and, if so, starts the entry over
 * at `loading`, the submenu's spinner. A question already on its way is not
 * asked twice. */
export function wantReads(
  reads: Record<string, ReadsEntry>,
  channelId: number,
  seq: number,
): boolean {
  const key = readsKey(channelId, seq);
  const entry = reads[key];
  const ask = entry === undefined || entry.t === "failed" || (entry.t === "ready" && entry.stale);
  if (ask) reads[key] = { t: "loading", stale: false };
  return ask;
}

/** The gateway answered about `seq` of `channelId` — the list, or `null` for
 * a read that failed. An answer overtaken on its way by a `seen` frame is
 * drawn, and asked for again at the next opening. Answers whether it landed:
 * nobody may be waiting any more. */
export function readsLanded(
  reads: Record<string, ReadsEntry>,
  channelId: number,
  seq: number,
  answer: MessageRead[] | null,
): boolean {
  const key = readsKey(channelId, seq);
  const entry = reads[key];
  if (entry?.t !== "loading") return false;
  reads[key] =
    answer === null ? { t: "failed" } : { t: "ready", reads: answer, stale: entry.stale };
  return true;
}

/** A reader's mark in `channelId` moved from `after` to `upto` (the `seen`
 * frame): the messages in `(after, upto]` have a reader they did not have, and
 * are asked about again at their next opening. Nothing drawn changes until
 * then. Answers whether any entry was touched. */
export function readsSeen(
  reads: Record<string, ReadsEntry>,
  channelId: number,
  after: number,
  upto: number,
): boolean {
  let touched = false;
  for (const [key, entry] of Object.entries(reads)) {
    if (entry.t === "failed" || entry.stale) continue;
    const [channel, seq] = key.split(":").map(Number);
    if (channel === channelId && seq! > after && seq! <= upto) {
      entry.stale = true;
      touched = true;
    }
  }
  return touched;
}

/** The socket was down and `seen` frames may have been missed: every list is
 * asked for again at its next opening. */
export function forgetReads(reads: Record<string, ReadsEntry>): void {
  for (const entry of Object.values(reads)) {
    if (entry.t !== "failed") entry.stale = true;
  }
}

/** When somebody read one of the reader's messages, under their name in its
 * «Details» (chat-plan.md D423): «today at 11:51», «yesterday at 23:40», the
 * date otherwise and its year once that differs — the desktop's
 * `time::read_stamp`. The gateway keeps the minute and nothing finer (D422). */
export function readStamp(atMs: number, nowMs: number, t: Dict): string {
  const at = new Date(atMs);
  const now = new Date(nowMs);
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const clock = readClock(at);
  const day = sameLocalDay(at, now)
    ? t.readToday()
    : sameLocalDay(at, yesterday)
      ? t.readYesterday()
      : at.toLocaleDateString(
          undefined,
          at.getFullYear() === now.getFullYear()
            ? { day: "numeric", month: "long" }
            : { day: "numeric", month: "long", year: "numeric" },
        );
  return t.readAt(day, clock);
}

/** When the other person in a direct conversation read an own message, at the
 * foot of its menu (chat-plan.md D466): «Read at 11:51» today, and before that
 * «Read yesterday at 23:40», «Read August 5 at 09:02» — `readStamp`'s day after
 * the word. Today's drops the day word: it is the common line, and at that
 * length it fits the menu as the menu was drawn before the time arrived. The
 * desktop's `time::read_line`. */
export function readLine(atMs: number, nowMs: number, t: Dict): string {
  return menuLine(atMs, nowMs, t, t.menuReadAt, t.menuReadOn);
}

/** When a message was last edited, at the foot of its menu for everybody who
 * reads it and in the tooltip of its pencil (chat-plan.md D467): «Edited at
 * 11:51» today, «Edited yesterday at 23:40», «Edited August 5 at 09:02» —
 * `readLine`'s shape, so the lines of one foot read alike. The desktop's
 * `time::edit_line`. */
export function editLine(atMs: number, nowMs: number, t: Dict): string {
  return menuLine(atMs, nowMs, t, t.menuEditedAt, t.menuEditedOn);
}

/** A dated line at the foot of a message's menu: `today` with the clock alone
 * on the reader's today, `other` with `readStamp`'s day and clock on any other
 * day. */
function menuLine(
  atMs: number,
  nowMs: number,
  t: Dict,
  today: (clock: string) => string,
  other: (when: string) => string,
): string {
  const at = new Date(atMs);
  return sameLocalDay(at, new Date(nowMs))
    ? today(readClock(at))
    : other(readStamp(atMs, nowMs, t));
}

/** Whether an edit to `text` under `entities` would leave the message as it
 * is — the same characters with the same formatting, under the card as it was
 * (chat-plan.md D467): no edit, so not sent. The entities are both in the
 * normal form the parse yields and the gateway stores; the desktop's
 * `Message::same_words`. */
export function editChangesNothing(
  message: Message,
  text: string,
  entities: readonly Entity[],
  noLinkPreview: boolean,
): boolean {
  const had = message.entities ?? [];
  return (
    message.text === text &&
    (message.no_link_preview ?? false) === noLinkPreview &&
    had.length === entities.length &&
    had.every((e, i) => sameEntity(e, entities[i]!))
  );
}

/** Two entities field for field, whatever order their keys came in. */
function sameEntity(a: Entity, b: Entity): boolean {
  const left = a as unknown as Record<string, unknown>;
  const right = b as unknown as Record<string, unknown>;
  const keys = Object.keys(left);
  return keys.length === Object.keys(right).length && keys.every((k) => left[k] === right[k]);
}

function sameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** The minute of a read, as the reader's locale writes it. */
function readClock(at: Date): string {
  return at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

// ---------------------------------------------------------------------------
// Feed

export function newBubble(message: Message, senderName: string, isOwn: boolean): Bubble {
  return {
    uiKey: message.nonce
      ? `${message.channel_id}:${message.sender_id}:nonce:${message.nonce}`
      : `${message.channel_id}:seq:${message.seq}`,
    message,
    senderName,
    isOwn,
    showHeader: true,
    state: "sent",
    sendProgress: 1,
    localPreviews: [],
    replyAuthor: null,
    replyPreview: null,
    replyGone: false,
    forwardAuthor: null,
    reactions: [],
    myReaction: null,
    comments: 0,
    views: 0,
    // An own bubble is the reader's words until resolveNames says whose a
    // message in a channel's name is (docs/broadcast-channels-plan.md D842).
    authored: isOwn,
  };
}

export function bubbleSeq(b: Bubble): number {
  return b.message.seq;
}

export function oldestSeq(feed: FeedState): number | undefined {
  return feed.bubbles[0]?.message.seq;
}

/** The ?after= cursor: newest bubble that is not the optimistic sentinel. */
export function newestSeq(feed: FeedState): number | undefined {
  for (let i = feed.bubbles.length - 1; i >= 0; i -= 1) {
    const seq = feed.bubbles[i]!.message.seq;
    if (seq !== PENDING_SEQ) return seq;
  }
  return undefined;
}

/** The newest CONFIRMED message — what the sidebar may quote once the one it
 * quoted is gone. Never an optimistic bubble: a send in flight is not
 * something the channel has said yet. */
export function newestMessage(feed: FeedState): Message | null {
  for (let i = feed.bubbles.length - 1; i >= 0; i -= 1) {
    const b = feed.bubbles[i]!;
    if (b.message.seq !== PENDING_SEQ) return b.message;
  }
  return null;
}

/** What a read mark may say: the newest REAL seq, and only when it moves the
 * mark forward. PENDING_SEQ posted once would poison the mark forever.
 *
 * A window that holds the channel's end says the CEILING instead (D277): a seq
 * between its newest bubble and `maxSeq` is a message that was deleted, and
 * whoever reached the end has read it as far as anybody can. Stopping at the
 * newest bubble left the mark short of `maxSeq` for good whenever the last
 * messages were deleted before they were read — nothing past the mark would
 * ever be on screen to move it, and the badge counted them until somebody
 * wrote something new. */
export function readableSeq(feed: FeedState): number | null {
  if (feed.reachedEnd) {
    const end = Math.max(feed.maxSeq, newestSeq(feed) ?? 0);
    return end > feed.lastReadSeq && end !== PENDING_SEQ ? end : null;
  }
  // The bottom is "everything", and the clamp below is what keeps PENDING_SEQ
  // out of it.
  return readableSeqUpTo(feed, Number.POSITIVE_INFINITY);
}

/** The same question for a reader who is NOT at the bottom: the mark earned by
 * messages passing under the eye, given everything up to `seen` has been on
 * screen (D249). Clamped to the newest real seq and not merely checked against
 * it — `seen` comes from a DOM id, and `POST /read` stores `max(existing, seq)`
 * without clamping, so a position this feed does not hold must not reach it.
 * `null` when the mark would not move, which is what keeps a scroll from
 * posting the same position on every event. */
export function readableSeqUpTo(feed: FeedState, seen: number): number | null {
  const newest = newestSeq(feed);
  if (newest === undefined) return null;
  const seq = Math.min(seen, newest);
  return seq > feed.lastReadSeq ? seq : null;
}

/** Derived, never accumulated — an accumulator was only ever reset.
 *
 * Counted off the window where the window can answer (D277): one that holds
 * the channel's end and starts no later than the first seq past the mark holds
 * every message past the mark there is, and a seq it lacks is a deletion — which
 * the subtraction went on counting as new for as long as the mark stood below. */
export function newBelow(feed: FeedState): number {
  const mark = feed.lastReadSeq;
  const oldest = oldestSeq(feed);
  const holdsAllPastMark =
    feed.reachedEnd && (feed.reachedStart || (oldest !== undefined && oldest <= mark + 1));
  if (holdsAllPastMark) {
    return feed.bubbles.filter((b) => b.message.seq !== PENDING_SEQ && b.message.seq > mark).length;
  }
  return Math.max(0, feed.maxSeq - mark);
}

/** Where the jump control takes a reader above the unread messages
 * (dioxus-plan.md D408): the first message past the read mark as it stands
 * now — where the reading stopped — or, while the window stops short of the
 * mark, the place just past it. Proved the way the desktop proves its unread
 * line: a read message above it in the window, the very next seq, or the top
 * of a channel held from its start. Otherwise the unread messages begin above
 * the window, and a reader anywhere in it has passed them — `null`, as for a
 * mark never set and for nothing unread. The desktop's `Feed::first_unread`. */
export function firstUnread(feed: FeedState): number | null {
  const mark = feed.lastReadSeq;
  if (mark === 0 || newBelow(feed) === 0) return null;
  const at = feed.bubbles.findIndex((b) => b.message.seq !== PENDING_SEQ && b.message.seq > mark);
  if (at === -1) return feed.reachedEnd ? null : mark + 1;
  const seq = feed.bubbles[at]!.message.seq;
  return at > 0 || feed.reachedStart || seq === mark + 1 ? seq : null;
}

/** Whether the window holds the channel's end once a page has landed (the
 * desktop's `end_holds`): it holds the newest message it has heard of, or the
 * page said the channel ends here — `saidSo` — and nothing arrived while it was
 * in flight (`askedAt` is the ceiling when it was asked for). The second way is
 * not the first because a deletion never lowers `maxSeq`: a channel whose last
 * messages are gone ends below its ceiling, and only a page can say so. */
export function endHolds(feed: FeedState, saidSo: boolean, askedAt: number): boolean {
  return (newestSeq(feed) ?? 0) >= feed.maxSeq || (saidSo && feed.maxSeq <= askedAt);
}

/** The ceiling as the served row has it after a reconnect. Below it the window
 * cannot tell a deleted message from a missed one, so it stops claiming the
 * end until the next page settles which (the desktop's `resync_ceiling`). An
 * answer read before the last frame says less than the frame did, and the
 * ceiling, being a counter, never comes down. */
export function resyncCeiling(feed: FeedState, maxSeq: number): void {
  feed.maxSeq = Math.max(feed.maxSeq, maxSeq);
  if ((newestSeq(feed) ?? 0) < feed.maxSeq) feed.reachedEnd = false;
}

/** A message this window could not place moved the ceiling to `seq` (the desktop's
 * `raise_ceiling`). It no longer holds the end: seqs it was never told about lie
 * below that one, missed and not deleted as far as anybody here knows (D277).
 * Answers whether it held the end until now, the runtime's cue to close the gap
 * (`closeGap`). A seq at or under the ceiling moves nothing: an edit of something
 * outside the window is not news about where the channel ends. */
function raiseCeiling(feed: FeedState, seq: number): boolean {
  if (seq <= feed.maxSeq) return false;
  feed.maxSeq = seq;
  const held = feed.reachedEnd;
  feed.reachedEnd = false;
  return held;
}

/** A page can hold messages the ceiling has not heard of: frames missed while the
 * socket was down. The ceiling is the channel's, not the socket's (the desktop's
 * `lift_ceiling`). */
export function liftCeiling(feed: FeedState): void {
  const newest = newestSeq(feed) ?? 0;
  if (newest > feed.maxSeq) feed.maxSeq = newest;
}

/** Whether a send of the reader's is still waiting for its answer here, as opposed
 * to one that failed, which waits for nothing. */
export function sendWaiting(feed: FeedState): boolean {
  return feed.bubbles.some((b) => b.message.seq === PENDING_SEQ && b.state !== "failed");
}

/** Full reset: everything but the four seeds goes back to defaults —
 * bubbles, divider, mode, the typing throttle, the flags. Who is typing is
 * every chat's (`UiState.typing`, D464) and stays. */
export function openFeed(
  feed: FeedState,
  channelId: number,
  name: string,
  maxSeq: number,
  lastReadSeq: number,
): void {
  feed.channelId = channelId;
  feed.channelName = name;
  feed.bubbles = [];
  feed.maxSeq = maxSeq;
  feed.lastReadSeq = lastReadSeq;
  feed.reachedStart = false;
  feed.reachedEnd = false;
  feed.loadingOlder = false;
  feed.loadingNewer = false;
  feed.loading = true;
  feed.drawnAhead = false;
  feed.dividerBefore = null;
  feed.mode = { t: "new" };
  feed.composerRevision += 1;
  // A selection is of one channel (forwarding.md D377).
  feed.selected = [];
  feed.error = null;
  feed.lastTypingSentMs = 0;
  // The post a link, or the way back from its comments, brought the last
  // window to was that window's (docs/broadcast-channels-plan.md D859): this
  // one lands where it opens, unless its own page names one (`openAround`).
  feed.focusSeq = null;
}

/** Whether the pane says «loading»: a window still waiting for its first page,
 * unless a page read ahead of the opening stands in it meanwhile
 * (docs/fast-start.md D1060) — the desktop's `Feed::shows_spinner`. */
export function showsSpinner(feed: FeedState): boolean {
  return feed.loading && !feed.drawnAhead;
}

/** Whether a page read ahead of an opening (D1060) may stand as the window: it
 * holds something, and it reaches down to the first unread message. A chat with
 * more unread than the page holds opens on two pages around the read mark instead,
 * and the newest ten would leave a hole between them. */
export function aheadDrawable(
  page: readonly Message[],
  maxSeq: number,
  lastReadSeq: number,
): boolean {
  const first = page[0]?.seq;
  if (first === undefined) return false;
  const unread = maxSeq > lastReadSeq && lastReadSeq > 0;
  return !unread || first <= lastReadSeq + 1;
}

/** Seq-dedup in both directions; the original wins; prepend keeps the page's
 * ascending order at the head. Does NOT touch maxSeq or reply previews.
 *
 * A page at the end lands in seq order, a send in flight last by construction
 * (`PENDING_SEQ`): it lands on whatever the window holds, a send included, and an
 * opening's older messages go above a page read ahead (D1060). */
export function appendMessages(feed: FeedState, page: Message[], atEnd: boolean): void {
  let index = 0;
  for (const m of page) {
    if (feed.bubbles.some((b) => b.message.seq === m.seq)) continue;
    const bubble = newBubble(m, "", false);
    if (atEnd) {
      feed.bubbles.push(bubble);
    } else {
      feed.bubbles.splice(index, 0, bubble);
      index += 1;
    }
  }
  if (atEnd) feed.bubbles.sort((a, b) => a.message.seq - b.message.seq);
  regroup(feed);
}

/** Where the confirmed run ends and what has not been confirmed begins: from here
 * on everything carries `PENDING_SEQ`, sends in flight and sends that failed. */
function tailStart(feed: FeedState): number {
  const at = feed.bubbles.findIndex((b) => b.message.seq === PENDING_SEQ);
  return at === -1 ? feed.bubbles.length : at;
}

/** After a prepend: drop the BOTTOM (the edge the reader scrolls away from),
 * and admit we no longer hold the end. Only confirmed history goes: a send in
 * flight sits behind the run, and its text lives nowhere else. */
export function trimTail(feed: FeedState): void {
  const confirmed = tailStart(feed);
  if (confirmed > WINDOW_SIZE) {
    feed.bubbles.splice(WINDOW_SIZE, confirmed - WINDOW_SIZE);
    feed.reachedEnd = false;
  }
}

/** After an append: drop the TOP and admit we no longer hold the start. */
export function trimHead(feed: FeedState): void {
  const confirmed = tailStart(feed);
  if (confirmed > WINDOW_SIZE) {
    feed.bubbles.splice(0, confirmed - WINDOW_SIZE);
    feed.reachedStart = false;
  }
}

/** The window is about to be rebuilt from one page (the desktop's
 * `Feed::replace_window`, D271): whatever run it held goes, a window being one run
 * and the page being somewhere else. What has not been confirmed stays: a send in
 * flight is not history, and it is still owed an answer that has to find it. The
 * unread line stays too, for when the window comes back to its message. */
export function leaveRun(feed: FeedState): void {
  feed.bubbles = feed.bubbles.filter((b) => b.message.seq === PENDING_SEQ);
}

/** The part of a page that falls inside the run the window holds. */
export function withinRun<P extends { messages: Message[] }>(feed: FeedState, page: P): P {
  const oldest = oldestSeq(feed);
  const newest = newestSeq(feed);
  if (oldest === undefined || newest === undefined) return { ...page, messages: [] };
  return { ...page, messages: page.messages.filter((m) => m.seq >= oldest && m.seq <= newest) };
}

/** Before anything else: every reply to this message quotes it (D273), wherever
 * the message itself is — an edit far up the history shows in the header of the
 * reply that is here. And a picked message is picked as it now stands (D377),
 * wherever the window is. */
function restate(feed: FeedState, server: Message, t: Dict): void {
  if (requote(feed, server)) fillReplyPreviews(feed, t);
  const picked = feed.selected.findIndex((m) => m.seq === server.seq);
  if (picked !== -1) feed.selected[picked] = server;
}

/** The send still waiting for this copy of its message: a nonce match on a pending
 * bubble. */
function pendingOf(feed: FeedState, server: Message): Bubble | undefined {
  return feed.bubbles.find(
    (b) => b.message.nonce !== "" && b.message.nonce === server.nonce && b.state !== "sent",
  );
}

/** The bubble object and its flags survive. */
function adopt(feed: FeedState, pending: Bubble, server: Message, t: Dict): void {
  // A history page may beat the send/WS ACK. Keep the optimistic rendering
  // identity and collapse its already-visible server copy with its chips.
  const duplicate = feed.bubbles.find((b) => b !== pending && b.message.seq === server.seq);
  if (duplicate) {
    pending.reactions = duplicate.reactions;
    pending.myReaction = duplicate.myReaction;
    feed.bubbles.splice(feed.bubbles.indexOf(duplicate), 1);
  }
  pending.message = server;
  pending.state = "sent";
  pending.sendProgress = 1;
  if (server.seq > feed.maxSeq) feed.maxSeq = server.seq;
  // Other messages can arrive while an upload is pending; the ACK restores
  // sequence order without changing the bubble's uiKey.
  feed.bubbles.sort((a, b) => a.message.seq - b.message.seq);
  regroup(feed);
  fillReplyPreviews(feed, t);
}

/** The echo merge: (a) adopt by nonce — the bubble object and its flags
 * survive; (b) replace by seq — the edit path, chips survive; (c) onto the end of
 * a window that reaches it, before any optimistic bubble by construction.
 *
 * A message joins only as the very next seq of a window that holds the channel's
 * end (the desktop's `Feed::reconcile`, D271; docs/fast-start.md D1091): that is a
 * proof, nothing can exist between the two. Anything else only raises the ceiling,
 * whether a frame past a hole or an edit of something outside the run. Placed
 * anyway it would be the cursor every later page is asked from, what lies between
 * would be skipped for good, and the read mark would jump it with them. A send of
 * ours is held to the same rule: confirmed out of turn it stays what it was, behind
 * the run, until the page that closes the gap brings it back under its nonce
 * (`foldServed`), a clock on it for one round trip longer being the whole cost.
 *
 * Answers whether the window LOST the end to this message: it held it, and now
 * knows of a message it could not place. */
export function reconcile(feed: FeedState, server: Message, t: Dict): boolean {
  restate(feed, server, t);
  const next = feed.reachedEnd && server.seq === feed.maxSeq + 1;
  const held = feed.bubbles.find((b) => b.message.seq === server.seq);
  // (a) nonce match on a still-pending bubble.
  const pending = pendingOf(feed, server);
  if (pending) {
    if (!next && !held) return raiseCeiling(feed, server.seq);
    adopt(feed, pending, server, t);
    return false;
  }
  // (b) seq match.
  if (held) {
    held.message = server;
    regroup(feed);
    fillReplyPreviews(feed, t);
    return false;
  }
  if (!next) return raiseCeiling(feed, server.seq);
  // (c) the next message.
  feed.maxSeq = server.seq;
  const at = feed.bubbles.findIndex((b) => b.message.seq > server.seq);
  feed.bubbles.splice(at === -1 ? feed.bubbles.length : at, 0, newBubble(server, "", false));
  trimHead(feed);
  regroup(feed);
  return false;
}

/** A page's copy of a message the window has a bubble for: (a) and (b) with no
 * rule about the end, a page being what closes the gap. Refreshes also restore
 * edits missed during disconnection, and a history page may itself be the first
 * acknowledgement of a pending send. */
export function foldServed(feed: FeedState, server: Message, t: Dict): void {
  restate(feed, server, t);
  const pending = pendingOf(feed, server);
  if (pending) {
    adopt(feed, pending, server, t);
    return;
  }
  const held = feed.bubbles.find((b) => b.message.seq === server.seq);
  if (held) {
    held.message = server;
    regroup(feed);
    fillReplyPreviews(feed, t);
  }
}

/** The row leaves the window (D253): a deleted message is a hole in the seq
 * run, and every cursor here reads a hole exactly as it reads a gap. */
export function applyDeletion(feed: FeedState, seq: number, t: Dict): void {
  // Every reply to it carries a quote of the words just withdrawn (D273). It
  // becomes the word that they are gone — before asking whether the window
  // holds the message, because a reply quotes its target wherever it is.
  let quoted = false;
  for (const b of feed.bubbles) {
    if (b.message.reply_to_seq === seq) {
      b.message.reply_to = { state: "deleted" };
      quoted = true;
    }
  }
  if (quoted) fillReplyPreviews(feed, t);
  // A message that is gone is not forwarded (D377), and the bar's count says
  // so — whether or not the window still holds it.
  if (feed.selected.some((m) => m.seq === seq)) {
    feed.selected = feed.selected.filter((m) => m.seq !== seq);
  }
  const at = feed.bubbles.findIndex((x) => x.message.seq === seq);
  if (at === -1) return;
  feed.bubbles.splice(at, 1);
  // The unread line hung off this message; left pointing at a bubble that is
  // gone it would simply stop being drawn.
  if (feed.dividerBefore === seq) insertUnreadDivider(feed);
  // The bubble above the hole may now start a run.
  regroup(feed);
}

/** Grouping: header when the sender changes — never on a pause, however long:
 * one person's consecutive messages are one block with one name and face (the
 * desktop's `Feed::regroup`). A service line shares a header with nothing and
 * breaks the run after it; the divider breaks runs; the date line is the
 * feed's own cut (`buildGroups`), since the day is the reader's local one. */
export function regroup(feed: FeedState): void {
  const divider = feed.dividerBefore;
  let prev: number | null = null;
  for (const b of feed.bubbles) {
    if (divider === b.message.seq) prev = null;
    if (b.message.service != null) {
      b.showHeader = true;
      prev = null;
      continue;
    }
    b.showHeader = prev !== b.message.sender_id;
    prev = b.message.sender_id;
  }
}

export function insertUnreadDivider(feed: FeedState): void {
  feed.dividerBefore = null;
  if (feed.lastReadSeq === 0 || feed.lastReadSeq >= feed.maxSeq) return;
  feed.dividerBefore =
    feed.bubbles.find((b) => b.message.seq > feed.lastReadSeq)?.message.seq ?? null;
  regroup(feed);
}

/** Once dismissed it stays gone for the life of the open channel. Returns
 * whether anything changed, so the caller can stay quiet on the scroll spam. */
export function dismissUnreadDivider(feed: FeedState): boolean {
  if (feed.dividerBefore === null) return false;
  feed.dividerBefore = null;
  regroup(feed);
  return true;
}

/** Resolve names + own-ness over the window; ends in fillReplyPreviews. */
export function resolveNames(feed: FeedState, dir: DirectoryState, t: Dict): void {
  const self = selfIdOf(dir);
  // What is said in a broadcast channel's name is the channel's
  // (docs/broadcast-channels-plan.md D841): its name where a person's goes.
  const voiced = (asChannel: boolean | undefined, id: number) =>
    asChannel === true ? (feed.voice?.name ?? "") : nameOf(dir, id);
  for (const b of feed.bubbles) {
    const m = b.message;
    b.senderName = voiced(m.as_channel, m.sender_id);
    // Drawn on the channel's side, the publisher's own post included (D863);
    // `sender_id` is 0 on such a message anyway.
    b.isOwn = self !== 0 && m.as_channel !== true && m.sender_id === self;
    b.authored = self !== 0 && authorOf(m) === self;
    // Whoever wrote what a reply answers is a name like these (D273).
    const quote = m.reply_to_seq != null ? m.reply_to : null;
    b.replyAuthor = quote?.state === "live" ? voiced(quote.as_channel, quote.sender_id) : null;
    // …and so is whoever wrote a forward's words (D376) — or the channel whose
    // words they are (D860). A header this build could not read names nobody
    // and keeps its line all the same.
    const forwarded = forwardedOf(m);
    b.forwardAuthor =
      forwarded === null
        ? null
        : forwarded.channel != null
          ? forwarded.channel.name
          : forwarded.sender_id !== 0
            ? nameOf(dir, forwarded.sender_id)
            : null;
  }
  fillReplyPreviews(feed, t);
}

/** `Message::author` (docs/broadcast-channels-plan.md D842): the person behind
 * a message in the channel's name when the reader's copy names them, its
 * sender otherwise. */
export function authorOf(m: Message): number {
  return m.posted_by ?? m.sender_id;
}

// ---------------------------------------------------------------------------
// Forwarding (docs/forwarding.md D374–D378)

/** A forward's header as this build can read it (D376): null on a message its
 * sender wrote, and one naming nobody (`sender_id` 0) for a header shaped past
 * what this build knows — what the Rust reader's `lossy_forwarded` makes of
 * it, here by hand, since nothing on this side checks a body against its
 * type. */
export function forwardedOf(m: Message): Forwarded | null {
  const raw: unknown = (m as { forwarded?: unknown }).forwarded;
  if (raw === undefined || raw === null) return null;
  const header = typeof raw === "object" ? (raw as Partial<Record<keyof Forwarded, unknown>>) : {};
  const sentAt = typeof header.sent_at_ms === "number" ? header.sent_at_ms : 0;
  // A broadcast channel's words (docs/broadcast-channels-plan.md D860): the
  // channel by name while it is public, the word that it is private otherwise.
  const channel = forwardChannelOf(header.channel);
  if (channel !== null) return { sender_id: 0, sent_at_ms: sentAt, channel };
  if (header.private_channel === true) {
    return { sender_id: 0, sent_at_ms: sentAt, private_channel: true };
  }
  if (typeof header.sender_id !== "number") return { sender_id: 0, sent_at_ms: 0 };
  return { sender_id: header.sender_id, sent_at_ms: sentAt };
}

/** A forward header's channel (D860), as this build can read it: an id, and
 * whatever of its name, handle, face and mark came with it. */
function forwardChannelOf(raw: unknown): ForwardChannel | null {
  if (raw === null || typeof raw !== "object") return null;
  const c = raw as Partial<Record<keyof ForwardChannel, unknown>>;
  if (typeof c.id !== "number") return null;
  const channel: ForwardChannel = { id: c.id, name: typeof c.name === "string" ? c.name : "" };
  if (typeof c.handle === "string") channel.handle = c.handle;
  if (typeof c.avatar_file_id === "string") channel.avatar_file_id = c.avatar_file_id;
  if (c.verified === true) channel.verified = true;
  return channel;
}

/** `Forwarded::of`: the header a copy of `m` carries (D374) — the one `m`
 * already has when it is itself a forward, so the line always names whoever
 * wrote the words; for words in a broadcast channel's name the channel — its
 * thread's for a comment (D860) — and its own author and time otherwise. */
export function forwardHeaderOf(m: Message): Forwarded {
  const held = forwardedOf(m);
  if (held !== null) return held;
  if (m.as_channel === true) {
    const id = m.thread_of?.channel_id ?? m.channel_id;
    return { sender_id: 0, sent_at_ms: m.sent_at_ms, channel: { id, name: "" } };
  }
  return { sender_id: m.sender_id, sent_at_ms: m.sent_at_ms };
}

/** `Message::forwardable` (D375): not a line the server wrote, and not words
 * their sender gave a lifetime. */
export function forwardable(m: Message): boolean {
  return m.service == null && m.ttl_s == null;
}

/** Whether a message may be picked (docs/message-selection.md D379): one the
 * server has numbered and did not write itself. A lifetime does not keep it
 * out — such a message cannot be forwarded, but it can be copied and deleted. */
export function selectable(m: Message): boolean {
  return m.seq !== PENDING_SEQ && m.service == null;
}

/** Picks the message at `seq`, or puts it back (D377, D379). Only a message
 * the window holds that may be picked at all is taken; nothing past
 * MAX_SELECTED. Returns whether the selection moved. */
export function toggleSelected(feed: FeedState, seq: number): boolean {
  const at = feed.selected.findIndex((m) => m.seq === seq);
  if (at !== -1) {
    feed.selected.splice(at, 1);
    return true;
  }
  if (feed.selected.length >= MAX_SELECTED || seq === PENDING_SEQ) return false;
  const bubble = feed.bubbles.find((b) => b.message.seq === seq);
  if (!bubble || !selectable(bubble.message)) return false;
  const after = feed.selected.findIndex((m) => m.seq > seq);
  feed.selected.splice(after === -1 ? feed.selected.length : after, 0, bubble.message);
  return true;
}

/** What the selection's bar offers (D379): each action only where it applies
 * to every pick — Telegram's rule, since a button that silently left some of
 * them behind would be worse than none. Deleting is not among them: it always
 * applies, anybody's message being the reader's to delete for themselves
 * (chat-plan.md D407); whether for everybody too is `deletableForEverybody`'s
 * question. */
export interface SelectionActions {
  forward: boolean;
  copy: boolean;
}

export function selectionActions(picked: readonly Message[]): SelectionActions {
  if (picked.length === 0) return { forward: false, copy: false };
  return {
    forward: picked.every(forwardable),
    copy: picked.some((m) => m.text !== ""),
  };
}

/** `dd.MM.yyyy HH:mm` in the reader's own zone — the head over each message
 * of a copied selection (D381), day first in both languages, as the desktop's
 * `time::clipboard_stamp`. */
export function clipboardStamp(ms: number): string {
  const at = new Date(ms);
  const two = (n: number) => String(n).padStart(2, "0");
  return `${two(at.getDate())}.${two(at.getMonth() + 1)}.${at.getFullYear()} ${two(at.getHours())}:${two(at.getMinutes())}`;
}

/** The picked messages as text for the clipboard (D381), the desktop's
 * `selection_text` line for line: only words travel; one message with words
 * is its words alone; several are a transcript — `Name, [dd.MM.yyyy HH:mm]`
 * over each one's words, a blank line between, a forward's words under its
 * own line — in the order said. Null when nothing picked has words. */
export function selectionText(
  picked: readonly Message[],
  nameOf: (id: number) => string,
  t: Dict,
): string | null {
  const worded = [...picked].filter((m) => m.text !== "").sort((a, b) => a.seq - b.seq);
  if (worded.length === 0) return null;
  if (worded.length === 1) return worded[0]!.text;
  return worded
    .map((m) => {
      let block = `${nameOf(m.sender_id)}, [${clipboardStamp(m.sent_at_ms)}]\n`;
      // A header this build could not read names nobody, as the bubble's own
      // line does (D376).
      const header = forwardedOf(m);
      if (header !== null) {
        block +=
          header.channel != null
            ? `${t.forwardedFrom()} ${header.channel.name}\n`
            : header.private_channel === true
              ? `${t.forwardedPrivateChannel()}\n`
              : header.sender_id === 0
                ? `${t.forwardedUnknown()}\n`
                : `${t.forwardedFrom()} ${nameOf(header.sender_id)}\n`;
      }
      return block + m.text;
    })
    .join("\n\n");
}

/** What forwarding `seqs` of this window sends (D377): each message as it was
 * picked when it was picked, as the window holds it otherwise — only the ones
 * that may go, ascending by seq, each once, at most MAX_FORWARD. */
export function forwardableOf(feed: FeedState, seqs: number[]): Message[] {
  const out: Message[] = [];
  for (const seq of [...new Set(seqs)].sort((a, b) => a - b)) {
    if (seq === PENDING_SEQ) continue;
    const m =
      feed.selected.find((x) => x.seq === seq) ??
      feed.bubbles.find((b) => b.message.seq === seq)?.message;
    if (m && forwardable(m)) out.push(m);
  }
  return out.slice(0, MAX_FORWARD);
}

/** Whose words a forward carries, each once, in the order they first come —
 * what the bar's first line names (D377): a person by the directory's name,
 * and a broadcast channel's words by the channel's
 * (docs/broadcast-channels-plan.md D860) — the name its header carries, or,
 * for words picked in the channel itself, its name as the forward was aimed
 * (`ForwardDraft.voice`). A header naming nobody this client can say — a
 * private channel's, one this build could not read — is the plain word, as
 * on the desktop's bar. */
export function draftAuthorNames(
  draft: ForwardDraft,
  nameOf: (id: number) => string,
  t: Dict,
): string[] {
  const voice = draft.voice;
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of draft.messages) {
    const header = forwardHeaderOf(m);
    const channel = header.channel ?? null;
    const person = channel === null && header.sender_id !== 0 ? header.sender_id : null;
    const name =
      person !== null
        ? nameOf(person)
        : channel !== null && channel.name !== ""
          ? channel.name
          : channel !== null && voice !== undefined && voice.channelId === channel.id
            ? voice.name
            : t.forwardedUnknown();
    // A person once by their id — two may share a name — and anything else
    // once by the words it reads as.
    const key = person !== null ? `person:${person}` : `words:${name}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }
  return out;
}

/** The waiting forward less a message of `channelId` that is gone (D377): the
 * gateway would refuse to copy it, and the bar must not count it. Null once
 * nothing is left — the bar comes down with its last message — and the same
 * draft when the deletion is nothing of its. */
export function forwardWithout(
  draft: ForwardDraft | null,
  channelId: number,
  seq: number,
): ForwardDraft | null {
  if (draft === null || draft.from !== channelId) return draft;
  if (!draft.messages.some((m) => m.seq === seq)) return draft;
  const messages = draft.messages.filter((m) => m.seq !== seq);
  return messages.length > 0 ? { ...draft, messages } : null;
}

/** The words of the header over every reply (D273), from the quote each reply
 * carries — the gateway's, kept current by requote() and applyDeletion(). The
 * target does not have to be in the window. A quote that says the target was
 * deleted is `replyGone`; a reply carrying nothing readable about its target
 * keeps a header without words. Neither has an author: the name is
 * resolveNames()'s, and it goes here with the words. */
export function fillReplyPreviews(feed: FeedState, t: Dict): void {
  for (const b of feed.bubbles) {
    const quote = b.message.reply_to_seq != null ? b.message.reply_to : null;
    const live = quote?.state === "live" ? quote : null;
    b.replyPreview = live ? quotePreview(live, t) : null;
    b.replyGone = quote?.state === "deleted";
    if (!live) b.replyAuthor = null;
  }
}

/** Writes the quote of `target` as it now stands over every reply to it in the
 * window (D273): its edit, off the socket or out of our own PATCH's answer.
 * Nobody sends the replies again — each client requotes from the edited
 * message's own frame. Returns whether any reply in the window quotes it. */
export function requote(feed: FeedState, target: Message): boolean {
  let quoted = false;
  for (const b of feed.bubbles) {
    if (b.message.reply_to_seq === target.seq) {
      b.message.reply_to = quoteOf(target);
      quoted = true;
    }
  }
  return quoted;
}

/** The page's sibling reaction keys, applied over [lo, hi] — INCLUDING to
 * empty: a chip somebody cleared while we were away is exactly the stale
 * decoration being healed (D160). */
export function applyPageReactions(
  feed: FeedState,
  span: [number, number],
  rows: SeqReactions[],
  mine: MyReaction[],
): void {
  const [lo, hi] = span;
  for (const b of feed.bubbles) {
    const seq = b.message.seq;
    if (seq < lo || seq > hi) continue;
    b.reactions = rows
      .filter((r) => r.seq === seq)
      .map((r) => ({ emoji: r.emoji, count: r.count, users: r.users }));
    b.myReaction = mine.find((m) => m.seq === seq)?.emoji ?? null;
  }
}

/** A page's comment counts (docs/broadcast-channels-plan.md D849), applied
 * over [lo, hi] as the chips are — a post the page lists no count for has
 * none, which is what the key's absence means. */
export function applyPageComments(
  feed: FeedState,
  span: [number, number],
  rows: CommentCount[],
): void {
  const [lo, hi] = span;
  for (const b of feed.bubbles) {
    const seq = b.message.seq;
    if (seq < lo || seq > hi) continue;
    const count = rows.find((r) => r.seq === seq)?.count ?? 0;
    if (b.comments !== count) b.comments = count;
  }
}

/** How many people have seen these posts (docs/broadcast-channels-plan.md
 * D1082), from a page or the answer to a report. A count only grows, so what
 * arrives late says less and changes nothing. */
export function applyViews(feed: FeedState, rows: ViewCount[]): void {
  for (const row of rows) {
    const b = feed.bubbles.find((x) => x.message.seq === row.seq);
    if (b && row.count > b.views) b.views = row.count;
  }
}

/** The `comments` frame (D849): a post's count, whole. */
export function applyComments(feed: FeedState, seq: number, count: number): void {
  const b = feed.bubbles.find((x) => x.message.seq === seq);
  if (b && b.comments !== count) b.comments = count;
}

export function applyReactions(feed: FeedState, seq: number, groups: ReactionGroup[]): void {
  const b = feed.bubbles.find((x) => x.message.seq === seq);
  if (!b) return;
  b.reactions = groups;
}

/** The reaction event: whole summary, never a delta — applying twice == once.
 * The actor being us (a second device) is how a chip becomes "mine". */
export function onReactionEvent(
  feed: FeedState,
  seq: number,
  userId: number,
  emoji: string | null,
  groups: ReactionGroup[],
  selfId: number,
): void {
  const b = feed.bubbles.find((x) => x.message.seq === seq);
  if (!b) return;
  b.reactions = groups;
  if (selfId !== 0 && userId === selfId) b.myReaction = emoji;
}

/** The optimistic mutator — remove-us-everywhere then add-us-once, never a
 * toggle, so the socket echo of our own change is a no-op. Returns the
 * previous value for rollback THROUGH THIS SAME FUNCTION. */
export function setMyReaction(
  feed: FeedState,
  seq: number,
  emoji: string | null,
  selfId: number,
): string | null {
  const b = feed.bubbles.find((x) => x.message.seq === seq);
  if (!b) return null;
  const previous = b.myReaction;
  b.myReaction = null;
  for (const g of b.reactions) {
    if (previous === g.emoji) g.count = Math.max(0, g.count - 1);
    g.users = g.users.filter((u) => u !== selfId);
  }
  b.reactions = b.reactions.filter((g) => g.count > 0);
  if (emoji !== null) {
    const g = b.reactions.find((x) => x.emoji === emoji);
    if (g) {
      g.count += 1;
      if (g.users.length < 3 && !g.users.includes(selfId)) g.users.push(selfId);
    } else {
      b.reactions.push({ emoji, count: 1, users: [selfId] });
    }
    b.myReaction = emoji;
  }
  return previous;
}

// Typing

export function shouldSendTyping(feed: FeedState, nowMs: number): boolean {
  if (nowMs - feed.lastTypingSentMs < TYPING_SEND_PERIOD_MS) return false;
  feed.lastTypingSentMs = nowMs;
  return true;
}

/** Who is typing where (chat-plan.md D464): channel → user → last frame. */
type TypingMap = UiState["typing"];

/** A frame: `userId` is typing in `channelId` — any chat, not only the open
 * one: the list draws every chat's line. Returns whether the line changed; a
 * frame from somebody already on it only moves their clock. Our own frames,
 * from another device, are not news about somebody else. */
export function onRemoteTyping(
  typing: TypingMap,
  channelId: number,
  userId: number,
  selfId: number,
  nowMs: number,
): boolean {
  if (selfId !== 0 && userId === selfId) return false;
  const users = (typing[channelId] ??= {});
  const fresh = users[userId] === undefined;
  users[userId] = nowMs;
  return fresh;
}

/** `userId`'s message arrived in `channelId`: what they were typing is in the
 * chat now, and their line goes with it rather than when the frame runs out
 * (D464). Returns whether they were on it. */
export function typingStopped(typing: TypingMap, channelId: number, userId: number): boolean {
  const users = typing[channelId];
  if (users?.[userId] === undefined) return false;
  delete users[userId];
  if (Object.keys(users).length === 0) delete typing[channelId];
  return true;
}

/** Ages the frames out — nobody says "stopped" on the wire. Returns whether
 * anybody left a line; a chat left with nobody has no entry. */
export function tickTyping(typing: TypingMap, nowMs: number): boolean {
  let changed = false;
  for (const ch of Object.keys(typing)) {
    const users = typing[Number(ch)]!;
    for (const u of Object.keys(users)) {
      if (nowMs - users[Number(u)]! >= TYPING_EXPIRY_MS) {
        delete users[Number(u)];
        changed = true;
      }
    }
    if (Object.keys(users).length === 0) delete typing[Number(ch)];
  }
  return changed;
}

/** Ascending user_id — the wording must not depend on hash order. */
export function typingUserIds(typing: TypingMap, channelId: number): number[] {
  return Object.keys(typing[channelId] ?? {})
    .map(Number)
    .sort((a, b) => a - b);
}

/** The words that stand in for a chat's subtitle in the header and for its
 * last message in the list (D464), or null while nobody is typing there: the
 * verb alone in a dialog, whose header and row already name the peer; one
 * name, two names or «several people» in a group. */
export function typingLine(
  typing: TypingMap,
  room: Room,
  dir: DirectoryState,
  t: Dict,
): string | null {
  const ids = typingUserIds(typing, room.channelId);
  if (ids.length === 0) return null;
  return t.typing(
    isDmRoom(room),
    ids.map((id) => nameOf(dir, id)),
  );
}

// ---------------------------------------------------------------------------
// Rooms

export function blankRoom(channelId: number, name: string): Room {
  return {
    channelId,
    ownerId: 0,
    kind: "standard",
    peerId: null,
    name,
    description: null,
    avatarFileId: null,
    preview: plainPreview(""),
    previewSeq: 0,
    previewBy: null,
    previewExpiresAtMs: null,
    line: { wanted: false, asking: false, epoch: 0, askedAt: null },
    timeText: "",
    unread: 0,
    maxSeq: 0,
    lastReadSeq: 0,
    digest: 0,
    seenSeq: 0,
    clearedSeq: 0,
    holes: [],
    lastMessageAtMs: 0,
    callMembers: [],
    callMemberIds: [],
    member: false,
    admin: false,
    settings: defaultSettings(),
    members: 0,
    joining: false,
    petStopped: false,
    handle: null,
    verified: false,
    rights: [],
    restricted: null,
    guest: false,
    noticeAtMs: 0,
  };
}

export function defaultSettings(): import("../gen/dto").ChannelSettings {
  return {
    is_private: false,
    max_voice_ms: 600_000,
    max_media_bytes: 104_857_600,
    max_message_chars: 4096,
    slowmode_s: 0,
    voice_enabled: true,
    channel_scheme_only: false,
    bg_blur: 0,
    bg_brightness: 100,
    // A broadcast channel's own (docs/broadcast-channels-plan.md D852): the
    // wire's defaults, which a gateway older than the keys means as well.
    comments_default: true,
    reactions_default: true,
    comment_reactions: true,
    comment_allows: [...COMMENT_KIND_WORDS],
  };
}

/** A public broadcast channel the reader reads without a subscription (D837,
 * D859): a row out of its card, which the list does not draw (`guest`). */
export function guestRoomOf(card: ChannelCard): Room {
  const row = blankRoom(card.channel_id, card.name);
  row.kind = card.kind ?? "standard";
  row.description = card.description ?? null;
  row.avatarFileId = card.avatar_file_id ?? null;
  row.members = card.members;
  row.member = card.member;
  row.settings.is_private = card.is_private;
  row.handle = card.handle ?? null;
  row.verified = card.verified === true;
  row.guest = true;
  return row;
}

/** Where the reader stands in a broadcast channel (D845): its owner — whose id
 * only the staff are served (D842), and the owner is staff — a server
 * administrator, the staff rights they hold. A thread asks its channel's row. */
export function standingIn(room: Room | undefined, serverAdmin: boolean, selfId: number): Standing {
  const owner = room !== undefined && selfId !== 0 && room.ownerId === selfId;
  return standingOf(owner, serverAdmin, rightsBits(room?.rights ?? []));
}

/** Until when `ability` is taken from the reader in this channel at `nowMs`
 * (D853): the restriction's end, `Infinity` for ever, or null when it is not
 * taken — a term that has run out reads as none (D493). */
export function restrictedUntil(
  room: Room | undefined,
  ability: Ability,
  nowMs: number,
): number | null {
  const r = room?.restricted;
  if (!r || !r.abilities.includes(ability)) return null;
  const until = r.until_ms ?? Number.POSITIVE_INFINITY;
  return until > nowMs ? until : null;
}

/** The voice of a broadcast channel (D841): what its row says of it. */
export function voiceOf(room: Room): ChannelVoice {
  return {
    channelId: room.channelId,
    name: room.name,
    avatarFileId: room.avatarFileId,
    verified: room.verified,
  };
}

/** The channel a handle names (D871), as far as this client knows: a row it
 * holds by that handle — blind to case, as handles are — or the directory's
 * answer to the handle once it has come; null until then, and for none. */
export function handleTarget(
  rooms: readonly Room[],
  cards: Record<string, ChannelCardEntry>,
  handle: string,
): number | null {
  const want = handle.toLowerCase();
  const held = rooms.find((r) => r.handle !== null && r.handle.toLowerCase() === want);
  if (held) return held.channelId;
  const entry = cards[handleCardKey(handle)];
  return entry?.t === "ready" ? entry.card.channel_id : null;
}

/** The first row the list draws — not a channel read without a subscription,
 * which is on no list (D837). */
export function firstListed(rooms: readonly Room[]): Room | undefined {
  return rooms.find((r) => !r.guest);
}

/** What the open window is, for the rules a broadcast channel brings
 * (docs/broadcast-channels-plan.md D850, D851, D867, D868): its own feed of
 * posts, a post's comments (D846), or any other chat — with the reader's
 * standing in the channel, which a thread asks of its channel's row. */
export type FeedPlace =
  | { t: "chat" }
  | { t: "posts"; room: Room; standing: Standing }
  | { t: "comments"; room: Room; standing: Standing; post: Message | null };

export function feedPlace(
  s: Pick<UiState, "rooms" | "feed" | "thread" | "account" | "directory">,
): FeedPlace {
  const self = selfIdOf(s.directory);
  if (s.thread !== null && s.thread.threadId === s.feed.channelId) {
    const room = roomOf(s.rooms, s.thread.channelId);
    if (room) {
      return {
        t: "comments",
        room,
        standing: standingIn(room, s.account.isAdmin, self),
        post: s.thread.post,
      };
    }
  }
  const room = roomOf(s.rooms, s.feed.channelId);
  if (room && isBroadcastRoom(room)) {
    return { t: "posts", room, standing: standingIn(room, s.account.isAdmin, self) };
  }
  return { t: "chat" };
}

/** Whether the reader may open the edit of this message now: not a line the
 * server wrote, a forward (D375) or a sticker (D550); a post within its day,
 * its publisher's while they hold `post` or any holder of `edit_posts`'s
 * (D850, D867); anything else its writer's within the hour (D409) — a
 * comment written in the channel's name included (D848), and no comment
 * while `comment` is taken from the reader in its channel: an edit is
 * writing (D923). On the gateway's clock, judged as the menu is drawn and
 * again at the press. */
export function editableIn(
  place: FeedPlace,
  b: Pick<Bubble, "message" | "authored">,
  selfId: number,
  serverNowMs: number,
): boolean {
  const m = b.message;
  if (m.seq === PENDING_SEQ || m.service != null || forwardedOf(m) !== null) return false;
  if (m.attachments?.[0]?.kind === "sticker") return false;
  if (place.t === "posts") return postEditableBy(m, selfId, place.standing, serverNowMs);
  if (place.t === "comments" && restrictedUntil(place.room, "comment", serverNowMs) !== null) {
    return false;
  }
  return b.authored && editWindowOpen(m, serverNowMs);
}

/** Whether a comment the reader writes in this channel may carry `kind` now
 * (D847, D852, D853, D924): the channel's comments take it
 * (`comment_allows`), and no restriction of the reader's takes it. What a
 * thread's comment box offers, door by door, and what it stages. */
export function commentTakes(room: Room, kind: Ability, serverNowMs: number): boolean {
  return (
    room.settings.comment_allows.includes(kind) && restrictedUntil(room, kind, serverNowMs) === null
  );
}

/** Whether the reader may delete a message of a broadcast channel now — for
 * everybody, the only way anything there is deleted (D1022): a post by its
 * publisher holding `post` and by `delete_posts` (D850), a comment by its
 * writer within four hours and by its moderators at any age (D868). Nobody
 * else deletes it at all. `null` outside a broadcast channel, whose rules are
 * `deletableForEverybodyBy`'s (D407, D410). */
export function channelDeletable(
  place: FeedPlace,
  m: Message,
  selfId: number,
  serverNowMs: number,
): boolean | null {
  switch (place.t) {
    case "chat":
      return null;
    case "posts":
      return postDeletableBy(m, selfId, place.standing);
    case "comments":
      return commentDeletableForEverybodyBy(m, selfId, place.standing, serverNowMs);
  }
}

export function roomOf(rooms: Room[], channelId: number): Room | undefined {
  return rooms.find((r) => r.channelId === channelId);
}

/** THE unread formula — one owner, clamped at zero (u64 wrapped once). Less
 * the holes seen opening past the mark (D277), which the server's subtraction
 * still counts; one above this row's ceiling — the deletion of a message whose
 * own frame never came — waits until the ceiling passes it, because until
 * then it was never counted. The mark forgets what it has passed. */
export function refreshUnread(room: Room): void {
  const mark = room.lastReadSeq;
  // Reassigned only when something goes: a fresh array on every refresh would
  // read to Immer as a changed row on every message in every channel.
  if (room.holes.some((seq) => seq <= mark)) room.holes = room.holes.filter((seq) => seq > mark);
  const holes = room.holes.filter((seq) => seq <= room.maxSeq).length;
  room.unread = Math.max(0, room.maxSeq - mark - holes);
}

/** The line and its stamp, from the message they quote — written together, so
 * a line can never quote one message under another's time. And its deadline
 * with them (D298), so a line can never outlive the message it quotes. */
function quote(room: Room, m: Message, nowMs: number, t: Dict): void {
  room.preview = preview(m, t);
  room.previewBy = bylineOf(m);
  room.previewSeq = m.seq;
  room.previewExpiresAtMs = m.expires_at_ms ?? null;
  room.timeText = sidebarStamp(m.sent_at_ms, nowMs, t);
  room.lastMessageAtMs = m.sent_at_ms;
  // A line with words on it is not waiting for any (D468).
  if (room.line.wanted) room.line.wanted = false;
}

/** The line and its stamp go blank, the order kept. */
function blankLine(room: Room): void {
  room.preview = plainPreview("");
  room.previewBy = null;
  room.previewSeq = 0;
  room.previewExpiresAtMs = null;
  room.timeText = "";
}

/** Something moved in the chat that an answer already on its way may not have
 * seen (D468): it will be asked again. Only while the line waits for one — no
 * other line has a question out that could be wrong, and a row that changes on
 * every edit is a row the sidebar redraws on every edit. */
function stirLine(room: Room): void {
  if (room.line.wanted) room.line.epoch += 1;
}

/** Both halves together, always — "2 on the header over a panel saying 0"
 * was a real bug. */
export function setRoomCall(room: Room, participants: number[], dir: DirectoryState): void {
  room.callMemberIds = [...participants];
  room.callMembers = participants.map((id) => nameOf(dir, id));
}

/** unread first, then recency, then name (case-folded). Stable. A security
 * card waiting in the official dialog counts as its unread, and as its newest
 * message when it is newer (docs/official-pet-plan.md D943). */
export function resort(rooms: Room[]): void {
  const news = (r: Room) => Number(r.unread > 0 || r.noticeAtMs > 0);
  const at = (r: Room) => Math.max(r.lastMessageAtMs, r.noticeAtMs);
  rooms.sort(
    (a, b) =>
      news(b) - news(a) ||
      at(b) - at(a) ||
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
  );
}

/** Rows folded in, which may be only some of the list (docs/fast-start.md D1053,
 * D1059). Idempotent, and the reconnect path: rows are patched from the answer
 * and nothing else is touched — a row left out of it is one this client holds as
 * it is, so only `retainListed`, given the whole list, ever drops anything. The
 * row-level door pair is written AFTER the settings object (it is public and
 * carries for non-members too). */
export function applyRows(
  rooms: Room[],
  channels: readonly ChannelState[],
  dir: DirectoryState,
  nowMs: number,
  t: Dict,
): void {
  for (const s of channels) {
    let row = roomOf(rooms, s.channel_id);
    if (!row) {
      row = blankRoom(s.channel_id, "");
      rooms.push(row);
    }
    row.name = s.name;
    row.ownerId = s.owner_id;
    row.kind = s.kind ?? "standard";
    row.peerId = s.peer_id ?? null;
    row.description = s.description ?? null;
    row.avatarFileId = s.avatar_file_id ?? null;
    row.maxSeq = s.max_seq;
    row.lastReadSeq = s.last_read_seq;
    // As of this answer, and frames do not move it (D1054): it says what the
    // row was when the gateway last vouched for it.
    row.digest = s.digest ?? 0;
    // The larger of the two (D422): a `seen` frame that landed while this
    // answer was on its way is newer than the answer. Absent is nobody, which
    // is also what a gateway older than the field means.
    row.seenSeq = Math.max(row.seenSeq, s.seen_seq ?? 0);
    // The same for a deletion of the chat (D465) and its frame.
    row.clearedSeq = Math.max(row.clearedSeq, s.cleared_seq ?? 0);
    row.member = s.member;
    row.admin = s.admin;
    row.settings = s.settings ?? defaultSettings();
    row.settings.is_private = s.is_private;
    row.members = s.members;
    // Present only while stopped (D368): absent is "not stopped", which is
    // also what a gateway without pets means by saying nothing.
    row.petStopped = s.pet_stopped === true;
    // A broadcast channel's (docs/broadcast-channels-plan.md D844, D853,
    // D857, D870), each absent when there is nothing to say. A row served is
    // on the list: a subscription made a preview an ordinary row.
    row.handle = s.handle ?? null;
    row.verified = s.verified === true;
    row.rights = s.rights ?? [];
    row.restricted = s.restricted ?? null;
    row.guest = false;
    refreshUnread(row);
    if (s.last_message) {
      quote(row, s.last_message, nowMs, t);
    } else {
      // Every message is gone: what a cold start draws, which the reconnect
      // path has to arrive at too.
      blankLine(row);
      row.lastMessageAtMs = 0;
      if (row.line.wanted) row.line.wanted = false;
    }
    setRoomCall(row, s.call?.participants ?? [], dir);
  }
  resort(rooms);
}

/** Drops what the whole list no longer holds (docs/fast-start.md D1053): `ids` is
 * every chat on it. */
export function retainListed(rooms: Room[], ids: ReadonlySet<number>): void {
  for (let i = rooms.length - 1; i >= 0; i -= 1) {
    const row = rooms[i]!;
    // A channel read without a subscription is never on the list (D837): it
    // stays for as long as it is being read.
    if (row.guest) continue;
    if (!ids.has(row.channelId)) rooms.splice(i, 1);
  }
}

/** What this client holds of the top of its list, as `known=` says it (D1054):
 * rows the gateway vouched for, in the order they are drawn. */
export function knownRows(rooms: readonly Room[], limit: number): Array<[number, number]> {
  const known: Array<[number, number]> = [];
  for (const row of rooms) {
    if (known.length === limit) break;
    if (!row.guest && row.digest !== 0) known.push([row.channelId, row.digest]);
  }
  return known;
}

/** Which chats of the whole list this client lacks a current row of (D1059), once
 * what the answer brought has been folded in. A row read without a subscription
 * counts as lacking: being listed, it is read as a subscriber's. */
export function staleRows(
  rooms: readonly Room[],
  chats: ReadonlyArray<readonly [number, number]>,
): number[] {
  const held = new Map(rooms.map((row) => [row.channelId, row]));
  const stale: number[] = [];
  for (const [id, digest] of chats) {
    const row = held.get(id);
    if (row === undefined || row.guest || row.digest !== digest) stale.push(id);
  }
  return stale;
}

/** The message arm of the sidebar. Own message bumps the MARK (not skips the
 * recompute); someone else's in the open channel bumps NEITHER — unread
 * keeps its value while max_seq rises. */
export function roomsOnMessage(
  rooms: Room[],
  m: Message,
  openChannel: number | null,
  own: boolean,
  nowMs: number,
  t: Dict,
): void {
  const row = roomOf(rooms, m.channel_id);
  if (!row) return;
  row.maxSeq = Math.max(row.maxSeq, m.seq);
  // The line quotes the newest message; two frames can swap on the way.
  if (m.seq >= row.previewSeq) quote(row, m, nowMs, t);
  if (own) {
    row.lastReadSeq = Math.max(row.lastReadSeq, m.seq);
    refreshUnread(row);
  } else if (m.channel_id !== openChannel) {
    refreshUnread(row);
  }
  resort(rooms);
}

/** Preview changes only when the edited message IS the one the line quotes.
 * No resort. */
export function roomsOnEdited(rooms: Room[], m: Message, t: Dict): void {
  const row = roomOf(rooms, m.channel_id);
  if (!row) return;
  // An answer on its way may carry the words from before (D468).
  stirLine(row);
  if (row.previewSeq === m.seq) {
    row.preview = preview(m, t);
    row.previewBy = bylineOf(m);
  }
}

/** The same for a deletion (D253): the row is a copy of one message's words,
 * so deleting that message and leaving the row alone keeps them legible in the
 * sidebar after they have gone from the chat. Matched on `previewSeq`, not
 * `maxSeq`, which stops naming the quoted message once the newest one goes —
 * four messages deleted from the bottom up left the second one's words on the
 * row. `replacement` is the message that is newest now — only the open channel
 * can answer that. Without one the line and its stamp go blank, the row keeps
 * its place in the order, and the line wants an answer from the gateway
 * (`askLines`, D468) — it used to stay blank until the next sync, a reconnect
 * away, the chat standing in the list with its name alone.
 *
 * And a deleted message past the read mark is not unread (D277): it comes off
 * the badge of a row that is not open. The open one's badge does not move on
 * traffic (roomsOnMessage); the hole counts at the next read mark. Once only —
 * our own deletion is folded in on the answer and again on the frame, and a
 * disappearing message at its deadline by our own clock and again on the
 * gateway's `message_deleted` (D298). */
export function roomsOnDeleted(
  rooms: Room[],
  channelId: number,
  seq: number,
  replacement: Message | null,
  openChannel: number | null,
  nowMs: number,
  t: Dict,
): void {
  const row = roomOf(rooms, channelId);
  if (!row) return;
  if (row.previewSeq === seq) {
    if (replacement) {
      quote(row, replacement, nowMs, t);
    } else {
      blankLine(row);
      row.line.wanted = true;
      row.line.askedAt = null;
    }
  }
  // An answer on its way may name this very message (D468).
  stirLine(row);
  if (seq > row.lastReadSeq && !row.holes.includes(seq)) {
    row.holes.push(seq);
    if (channelId !== openChannel) refreshUnread(row);
  }
  resort(rooms);
}

/** A line's question, as `askLines` hands it to the runtime (D468). */
export interface LineQuestion {
  channelId: number;
  epoch: number;
}

/** What came of an answer to a line's question (D468): the line was drawn;
 * nothing to draw (filled meanwhile, the row gone, the question refused, or
 * nothing on the page the line may quote); or the chat moved while it was out,
 * and it is asked again under `epoch`. */
export type LineAnswer = { t: "drawn" } | { t: "settled" } | { t: "again"; epoch: number };

function askable(row: Room): boolean {
  const line = row.line;
  return line.wanted && !line.asking && line.askedAt !== line.epoch;
}

/** Whether any line is blank for want of a message to quote, with no question
 * out for it and the chat moved since the last one — read before every
 * `askLines`, so a publish that wants nothing costs one pass and no draft. */
export function hasLineQuestion(rooms: readonly Room[]): boolean {
  return rooms.some(askable);
}

/** Claims the question of every line that wants one (D468) and answers which:
 * the runtime asks the gateway for each chat's newest page and hands it to
 * `answerLine` under the epoch. */
export function askLines(rooms: Room[]): LineQuestion[] {
  const asked: LineQuestion[] = [];
  for (const row of rooms) {
    if (!askable(row)) continue;
    row.line.asking = true;
    row.line.askedAt = row.line.epoch;
    asked.push({ channelId: row.channelId, epoch: row.line.epoch });
  }
  return asked;
}

/** The gateway's newest page for a line's question asked under `epoch`, or
 * null when the question was refused (D468). The line takes the newest message
 * on the page — a service line as readily as anybody's words — that is neither
 * cleared (D465) nor past its deadline on the gateway's clock (D297: pages are
 * not filtered for it). An empty page is a chat with nothing left in it for
 * this reader: the one case the line stays blank, as a cold start draws it. An
 * answer the chat overtook is not drawn — it may name a message gone since, or
 * carry its words from before an edit — and is asked again. */
export function answerLine(
  rooms: Room[],
  channelId: number,
  epoch: number,
  page: readonly Message[] | null,
  serverNowMs: number,
  nowMs: number,
  t: Dict,
): LineAnswer {
  const row = roomOf(rooms, channelId);
  if (!row) return { t: "settled" };
  if (!row.line.wanted) {
    // A message arrived meanwhile, or a sync quoted the newest one.
    row.line.asking = false;
    return { t: "settled" };
  }
  if (row.line.epoch !== epoch) {
    row.line.askedAt = row.line.epoch;
    return { t: "again", epoch: row.line.epoch };
  }
  row.line.asking = false;
  if (page === null) return { t: "settled" };
  let newest: Message | null = null;
  for (const m of page) {
    if (m.channel_id !== channelId || m.seq <= row.clearedSeq) continue;
    const at = m.expires_at_ms;
    if (Number.isFinite(at) && (at as number) <= serverNowMs) continue;
    if (newest === null || m.seq > newest.seq) newest = m;
  }
  if (newest !== null) {
    quote(row, newest, nowMs, t);
  } else if (page.length === 0) {
    row.line.wanted = false;
    row.lastMessageAtMs = 0;
  } else {
    // Every message on the page is past its deadline and not swept yet: the
    // gateway's deletions are on their way, and each moves the epoch and asks
    // again.
    return { t: "settled" };
  }
  resort(rooms);
  return { t: "drawn" };
}

/** The runtime stops asking for a line without an answer it could draw (D468)
 * — the chat kept moving under every question. The line waits for the next
 * deletion or the next sync. */
export function dropLineQuestion(rooms: Room[], channelId: number): void {
  const row = roomOf(rooms, channelId);
  if (row) row.line.asking = false;
}

/** Monotonic: a stale frame cannot un-read a channel. Returns the row, or
 * undefined for an unknown channel (then nothing changed). */
export function roomsOnRead(rooms: Room[], channelId: number, effective: number): Room | undefined {
  const row = roomOf(rooms, channelId);
  if (!row) return undefined;
  row.lastReadSeq = Math.max(row.lastReadSeq, effective);
  refreshUnread(row);
  resort(rooms);
  return row;
}

/** A repeat channel_created is ignored whole — the name is NOT updated — but
 * for a re-friend (friends-plan.md D411): the same conversation, kept
 * read-only since the friendship ended, is the two of them's again. */
export function roomsOnCreated(
  rooms: Room[],
  channelId: number,
  name: string,
  kind: string,
  peerId: number | null,
  createdBy: number,
  selfId: number,
): boolean {
  const existing = roomOf(rooms, channelId);
  if (existing) {
    if (kind !== "dm" || !isFormerDm(existing)) return false;
    existing.member = true;
    existing.members = 2;
    resort(rooms);
    return true;
  }
  const row = blankRoom(channelId, name);
  row.kind = kind;
  row.peerId = peerId;
  if (kind === "dm") {
    row.member = true;
    row.members = 2;
  } else if (createdBy !== 0) {
    row.members = 1;
    row.member = selfId !== 0 && createdBy === selfId;
    row.admin = row.member;
    // Its maker owns it (D109): what the maker's standing in a broadcast
    // channel is read from on their other tabs, before any state lists it
    // (docs/broadcast-channels-plan.md D845).
    row.ownerId = createdBy;
  }
  rooms.push(row);
  resort(rooms);
  return isDmRoom(row) || (createdBy !== 0 && createdBy === selfId);
}

/** The friendship behind a direct row ended over a conversation with something
 * in it (friends-plan.md D411): the row stays, readable, and nobody is a member
 * of it any more — which moves it off Friends to All alone and puts the
 * read-only bar where the composer was. The gateway has already ended any
 * call in it. False when there was no such row. */
export function roomsOnDmEnded(rooms: Room[], channelId: number): boolean {
  const row = roomOf(rooms, channelId);
  if (!row || !isDmRoom(row)) return false;
  row.member = false;
  row.admin = false;
  row.members = 0;
  row.joining = false;
  return true;
}

/** The reader deleted this chat for themselves up to `throughSeq`
 * (friends-plan.md D465) — on this device, or on another (the
 * `history_cleared` frame). The row stays on every tab it was on, emptied:
 * the line goes when it quotes a message at or below the mark, and nothing up
 * to the mark is unread any more. False when the mark did not move — a
 * repeat, or no such row. */
export function roomsOnHistoryCleared(
  rooms: Room[],
  channelId: number,
  throughSeq: number,
): boolean {
  const row = roomOf(rooms, channelId);
  if (!row || throughSeq <= row.clearedSeq) return false;
  row.clearedSeq = throughSeq;
  // An answer on its way may quote what is cleared now (D468).
  stirLine(row);
  if (row.previewSeq <= throughSeq) {
    blankLine(row);
    row.lastMessageAtMs = 0;
  }
  // Gone is read by definition (D299): the gateway answers the mark moved
  // over it, and so does the row until it next asks.
  row.lastReadSeq = Math.max(row.lastReadSeq, throughSeq);
  refreshUnread(row);
  resort(rooms);
  return true;
}

/** The whole profile travels, never a delta: description null means cleared.
 * kind/peer_id thread through so a DM's rename cannot turn it into an
 * ordinary channel named after somebody. */
export function roomsOnUpdated(
  rooms: Room[],
  e: {
    channel_id: number;
    owner_id: number;
    name: string;
    kind?: string;
    peer_id?: number | null;
    description: string | null;
    avatar_file_id: string | null;
    is_private: boolean;
    handle?: string | null;
    verified?: boolean;
  },
): void {
  const row = roomOf(rooms, e.channel_id);
  if (!row) return;
  row.name = e.name;
  row.ownerId = e.owner_id;
  row.kind = e.kind ?? "standard";
  row.peerId = e.peer_id ?? null;
  row.description = e.description;
  row.avatarFileId = e.avatar_file_id;
  row.settings.is_private = e.is_private;
  // Whole-profile like the rest (D870, D857): absent is «none».
  row.handle = e.handle ?? null;
  row.verified = e.verified === true;
  resort(rooms);
}

/** What is taken from somebody in a broadcast channel moved (the `restricted`
 * frame, docs/broadcast-channels-plan.md D853): only the reader's own is
 * kept on the row — the staff hear of everybody's, which the web's screens
 * for them do not draw yet (D865). An empty list is a restriction lifted. */
export function roomsOnRestricted(
  rooms: Room[],
  channelId: number,
  userId: number,
  abilities: string[],
  untilMs: number | null,
  selfId: number,
): boolean {
  const row = roomOf(rooms, channelId);
  if (!row || selfId === 0 || userId !== selfId) return false;
  row.restricted = abilities.length > 0 ? { abilities, until_ms: untilMs } : null;
  return true;
}

/** The reader's restrictions whose end has come on the gateway's clock
 * (D853): no frame says so — the client holds the deadline — and a term that
 * has run out reads as none (D493). The row lets it go, so whatever its foot
 * and its menus draw from it is drawn again. Touches nothing when none has
 * ended. */
export function endRestrictions(rooms: Room[], serverNowMs: number): void {
  for (const row of rooms) {
    const until = row.restricted?.until_ms;
    if (until != null && until <= serverNowMs) row.restricted = null;
  }
}

/** Membership: `counted` makes this commutative with onJoined in either
 * order — one join, one head. Returns whether it was about us. */
export function roomsOnMembership(
  rooms: Room[],
  channelId: number,
  userId: number,
  change: string,
  admin: boolean,
  selfId: number,
  rights: string[] = [],
): boolean {
  const row = roomOf(rooms, channelId);
  if (!row) return false;
  const mine = selfId !== 0 && userId === selfId;
  const counted = mine && row.member;
  const stays = change !== "left";
  if (mine) {
    row.member = stays;
    row.admin = stays && admin;
    row.joining = false;
    // A broadcast channel's staff rights (D844) ride the frame whole.
    row.rights = stays ? rights : [];
  }
  if (change === "joined") {
    if (!counted) row.members += 1;
  } else if (change === "left") {
    row.members = Math.max(0, row.members - 1);
  }
  return mine;
}

/** Folds what POST /join itself answered — never wait for the broadcast,
 * which only comes when the join changed something (the creator's row is
 * exactly the case that would wait forever). */
export function roomsOnJoined(rooms: Room[], channelId: number, admin: boolean): void {
  const row = roomOf(rooms, channelId);
  if (!row) return;
  row.joining = false;
  if (!row.member) row.members += 1;
  row.member = true;
  row.admin = admin;
}

/** The card under a link to a channel (D279): the reader's own row when they
 * hold one — every public channel and every channel they are in — and the
 * gateway's card (D281) for one they do not, once it has answered; `null`
 * until then, and for a channel with no card at all. A direct conversation
 * wears its peer's face, as its sidebar row does. */
export function channelLinkView(
  rooms: Room[],
  cards: Record<string, ChannelCardEntry>,
  dir: DirectoryState,
  channelId: number,
  token: string | null,
): ChannelLinkView | null {
  const row = roomOf(rooms, channelId);
  if (row) {
    const peer = isDmRoom(row) && row.peerId !== null ? met(dir, row.peerId) : undefined;
    return {
      held: true,
      kind: row.kind,
      name: row.name,
      description: row.description,
      avatarFileId: peer?.avatarFileId ?? row.avatarFileId,
      faceCreatedAt: peer?.createdAt ?? 0,
      members: row.members,
      member: row.member,
      isPrivate: row.settings.is_private,
      joining: row.joining,
      handle: row.handle,
      verified: row.verified,
    };
  }
  const entry = cards[channelCardKey(channelId, token)];
  if (entry?.t !== "ready") return null;
  const card = entry.card;
  return {
    held: false,
    kind: card.kind ?? "standard",
    name: card.name,
    description: card.description ?? null,
    avatarFileId: card.avatar_file_id ?? null,
    faceCreatedAt: 0,
    members: card.members,
    member: card.member,
    isPrivate: card.is_private,
    joining: false,
    handle: card.handle ?? null,
    verified: card.verified === true,
  };
}

/** What a link pasted into the sidebar's search names (chat-plan.md D526,
 * D530), in the sidebar's shapes: a row the reader holds, somebody under the
 * search's caption, a group outside the list — drawn from its card
 * (`channelLinkView`) once that has come — or a sticker pack, read as its row
 * is shown (docs/stickers-plan.md D562). */
export type LinkFound =
  | { t: "room"; room: Room }
  | { t: "person"; userId: number }
  | { t: "outside"; channelId: number; token: string | null }
  | { t: "pack"; packId: number }
  /** A broadcast channel by its handle (docs/broadcast-channels-plan.md
   * D871), asked of the directory as the row is shown. */
  | { t: "handle"; handle: string };

/** The desktop's `components::search::named_by`. A person the reader talks to
 * is that dialog's row, as a search by name finds them; anybody else the
 * directory holds is a person, the reader included. `null` for nothing: an id
 * the directory holds no row of — asked about by this read (docs/fast-start.md
 * D1057), and found when the answer draws this again — a retired pet or any pet
 * on a server without them (D365), and a group the gateway has already said is
 * not there for this reader — the 404 of D114, kept as «none» with no time to ask
 * again. */
export function linkFound(
  link: AppLink,
  rooms: Room[],
  dir: DirectoryState,
  cards: Record<string, ChannelCardEntry>,
  petsOn: boolean,
): LinkFound | null {
  const somebody = (userId: number): LinkFound | null => {
    const p = met(dir, userId);
    if (p === undefined) return null;
    if (isPet(p) && (!petsOn || p.retired === true)) return null;
    const dm = rooms.find((r) => isDmRoom(r) && r.peerId === userId);
    return dm ? { t: "room", room: dm } : { t: "person", userId };
  };
  if (link.kind === "user") return somebody(link.userId);
  if (link.kind === "stickerPack") return { t: "pack", packId: link.packId };
  if (link.kind === "handle") {
    const held = rooms.find(
      (r) => r.handle !== null && r.handle.toLowerCase() === link.handle.toLowerCase(),
    );
    return held && !held.guest ? { t: "room", room: held } : { t: "handle", handle: link.handle };
  }
  const row = roomOf(rooms, link.channelId);
  // A channel read without a subscription is on no list (D837): its link
  // finds it from its card, as one the reader never opened.
  if (row && !row.guest) {
    return isDmRoom(row) && row.peerId !== null ? somebody(row.peerId) : { t: "room", room: row };
  }
  const token = link.kind === "invite" ? link.token : null;
  const entry = cards[channelCardKey(link.channelId, token)];
  if (entry?.t === "none" && entry.retryAt === null) return null;
  return { t: "outside", channelId: link.channelId, token };
}

// ---------------------------------------------------------------------------
// Disappearing messages (docs/disappearing-messages.md D298)

/** One more reading of the gateway's clock, against this machine's as it
 * arrived: the `sent_at_ms` of a message handed over the moment it was stored
 * — a live frame, the answer to a send — or the `server_time_ms` of a
 * `GET /v1/bootstrap` answer. Never a history page, and never an edit's frame:
 * their `sent_at_ms` is as old as the message. The offset is the largest of
 * the newest `CLOCK_SAMPLES`, the reading the network delayed least. */
export function noteClockSample(clock: ClockState, serverMs: number, nowMs: number): void {
  const sample = serverMs - nowMs;
  if (!Number.isFinite(sample)) return;
  clock.samples.push(sample);
  if (clock.samples.length > CLOCK_SAMPLES) {
    clock.samples.splice(0, clock.samples.length - CLOCK_SAMPLES);
  }
  clock.offsetMs = Math.max(...clock.samples);
}

/** The gateway's now, as near as this client can tell — the clock every
 * `expires_at_ms` is written on. */
export function serverNow(clock: ClockState, nowMs: number): number {
  return nowMs + clock.offsetMs;
}

/** Whether no more than `EDIT_WINDOW_MS` has passed since the message's send
 * at `serverNowMs` — the gateway's now, the clock `sent_at_ms` is written on
 * (D409); `Message::edit_window_open` on the Rust side. Says nothing of whose
 * it is, nor of a forward. */
export function editWindowOpen(m: Message, serverNowMs: number): boolean {
  return serverNowMs - m.sent_at_ms <= EDIT_WINDOW_MS;
}

/** Whether its author deleting the message at `serverNowMs` still takes it
 * from everybody: no more than `DELETE_WINDOW_MS` since its send (chat-plan.md
 * D410); `Message::delete_window_open` on the Rust side. Says nothing of a
 * moderator's deletion, which is everybody's at any age (D407). */
export function deleteWindowOpen(m: Message, serverNowMs: number): boolean {
  return serverNowMs - m.sent_at_ms <= DELETE_WINDOW_MS;
}

/** Where the reader stands among a chat's messages (chat-plan.md D407) —
 * `echo_wire::dto::DeleteStanding`: a moderator takes any message from
 * everybody at any age; either of a dialog's two anybody's within four hours
 * of its send; a member their own within them (D410). */
export type DeleteStanding = "member" | "peer" | "moderator";

/** The reader's standing in `room` (D407): in a dialog a peer — nobody
 * moderates one (D177), a server admin in their own included; elsewhere a
 * moderator where they administer it (`admin`: its own admin or owner) or the
 * server, a member otherwise. `Room::delete_standing` on the desktop; the
 * gateway's own rule, which it applies again at the press. */
export function deleteStanding(room: Room | undefined, serverAdmin: boolean): DeleteStanding {
  // A former friend's conversation (friends-plan.md D411) is nobody's dialog
  // any more: what is left is a member's own messages, as the gateway's
  // `standing_in` says of a direct channel the reader is not in.
  if (room !== undefined && isDmRoom(room)) return room.member ? "peer" : "member";
  return (room?.admin ?? false) || serverAdmin ? "moderator" : "member";
}

/** `deleteStanding`, with the official dialog's copies only the reader's to
 * hide (docs/official-pet-plan.md D937, D1020): an announcement is taken back
 * from everybody by its admin's «Удалить у всех получателей» on the desktop
 * (D944), never by «для всех» on somebody's copy. `Ui::deletes_for_everybody`
 * on the desktop. */
export function deleteStandingIn(
  room: Room | undefined,
  dir: DirectoryState,
  serverAdmin: boolean,
): DeleteStanding {
  if (room !== undefined && isOfficialRoom(room, dir)) return "member";
  return deleteStanding(room, serverAdmin);
}

/** Whether `selfId`, standing as `standing`, may take the message from
 * everybody at `serverNowMs`, the gateway's now (chat-plan.md D407): a
 * moderator any message, either of a dialog's two any within four hours of
 * its send, anybody else their own within them (D410).
 * `Message::deletable_for_everybody_by` on the Rust side. */
export function deletableForEverybodyBy(
  m: Message,
  selfId: number,
  standing: DeleteStanding,
  serverNowMs: number,
): boolean {
  switch (standing) {
    case "moderator":
      return true;
    case "peer":
      return deleteWindowOpen(m, serverNowMs);
    case "member":
      return selfId !== 0 && m.sender_id === selfId && deleteWindowOpen(m, serverNowMs);
  }
}

/** Whether every pick may go for everybody (D407): what decides whether the
 * question before the deletion offers «for everybody» beside «for me», or
 * «for me» alone. `false` with nothing picked; `Feed::deletable_for_everybody`
 * on the desktop. */
export function deletableForEverybody(
  selected: readonly Message[],
  selfId: number,
  standing: DeleteStanding,
  serverNowMs: number,
): boolean {
  return (
    selected.length > 0 &&
    selected.every((m) => deletableForEverybodyBy(m, selfId, standing, serverNowMs))
  );
}

/** A copy of a message with a deadline that this client is showing. */
export interface TimedMessage {
  channelId: number;
  seq: number;
  expiresAtMs: number;
}

/** Every such copy there is: the open feed's bubbles, and the line of each
 * sidebar row — a row quotes its newest message's words whether or not its
 * channel is open. Only what the gateway has confirmed: an optimistic bubble's
 * deadline is this client's guess at one, and a send still on its way, or one
 * that failed, is not a message yet — it has nothing to disappear from. */
export function timedMessages(feed: FeedState, rooms: Room[]): TimedMessage[] {
  const timed: TimedMessage[] = [];
  for (const b of feed.bubbles) {
    const { seq, expires_at_ms: at } = b.message;
    // `isFinite` and not `!= null`: the JSON boundary is not shape-checked, and
    // a deadline that is not a number must read as none — never as one that is
    // always due, or never reached, for the timer to spin on.
    if (seq === PENDING_SEQ || !Number.isFinite(at)) continue;
    // Under the window's own channel, which is where the deletion looks for it.
    timed.push({ channelId: feed.channelId, seq, expiresAtMs: at as number });
  }
  for (const row of rooms) {
    const at = row.previewExpiresAtMs;
    if (row.previewSeq === 0 || !Number.isFinite(at)) continue;
    const held = timed.some((m) => m.channelId === row.channelId && m.seq === row.previewSeq);
    if (!held) {
      timed.push({ channelId: row.channelId, seq: row.previewSeq, expiresAtMs: at as number });
    }
  }
  return timed;
}

/** Writes the deadlines the gateway armed (D302) onto every copy of those
 * messages this client holds — the open feed's bubble and the sidebar row that
 * quotes it. Answers whether anything moved.
 *
 * Only ever onto a copy that has none: the gateway arms once and says so once,
 * but a socket that reconnects can replay a page over a frame, and «the first
 * reader started it» must not be rewritten by a second announcement. */
export function armExpiry(
  feed: FeedState,
  rooms: Room[],
  channelId: number,
  armed: readonly Expiring[],
): boolean {
  let moved = false;
  for (const e of armed) {
    if (!Number.isFinite(e.expires_at_ms)) continue;
    if (feed.channelId === channelId) {
      const b = feed.bubbles.find((b) => b.message.seq === e.seq);
      if (b && b.message.expires_at_ms == null) {
        b.message.expires_at_ms = e.expires_at_ms;
        moved = true;
      }
    }
    const row = rooms.find((r) => r.channelId === channelId);
    // An answer on its way may carry a message without its deadline (D468).
    if (row) stirLine(row);
    if (row && row.previewSeq === e.seq && row.previewExpiresAtMs == null) {
      row.previewExpiresAtMs = e.expires_at_ms;
      moved = true;
    }
  }
  return moved;
}

/** The copies past their deadline at `serverNowMs` — `Message::expired_at` on
 * the Rust side, at the deadline and not only after it. */
export function dueMessages(feed: FeedState, rooms: Room[], serverNowMs: number): TimedMessage[] {
  return timedMessages(feed, rooms).filter((m) => m.expiresAtMs <= serverNowMs);
}

/** The nearest deadline among them, on the gateway's clock, or null when
 * nothing held is going anywhere. */
export function nextDeadline(feed: FeedState, rooms: Room[]): number | null {
  let nearest: number | null = null;
  for (const m of timedMessages(feed, rooms)) {
    if (nearest === null || m.expiresAtMs < nearest) nearest = m.expiresAtMs;
  }
  return nearest;
}

// ---------------------------------------------------------------------------
// Friends

export type FriendNews =
  | { t: "requested"; userId: number }
  | {
      t: "accepted";
      userId: number;
    };

/** The snapshot writer (D174): one object, applied whole; the FIRST snapshot
 * seeds silently; the diff is computed against what we held, not claimed by
 * the frame. */
export function applyFriendsSnapshot(
  friends: FriendsState,
  snapshot: FriendsSnapshot,
): FriendNews[] {
  const news: FriendNews[] = [];
  if (friends.seeded) {
    for (const inc of snapshot.incoming) {
      if (!friends.incoming.some((r) => r.user_id === inc.user_id)) {
        news.push({ t: "requested", userId: inc.user_id });
      }
    }
    for (const f of snapshot.friends) {
      const wasFriend = friends.friends.some((x) => x.user_id === f.user_id);
      const wasOutgoing = friends.outgoing.includes(f.user_id);
      // Accepting one yourself is not news — only a request WE sent that
      // became a friendship is.
      if (!wasFriend && wasOutgoing) news.push({ t: "accepted", userId: f.user_id });
    }
  }
  friends.friends = snapshot.friends;
  friends.incoming = snapshot.incoming;
  friends.outgoing = snapshot.outgoing;
  friends.blocked = snapshot.blocked;
  friends.policy = snapshot.policy;
  friends.seeded = true;
  return news;
}

export function isFriend(friends: FriendsState, userId: number): boolean {
  return friends.friends.some((f) => f.user_id === userId);
}

// ---------------------------------------------------------------------------
// Presence

/** A transition frame: Offline deletes; anything else is stored as-is (an
 * unknown word is stored and simply draws nothing). */
export function applyPresenceFrame(
  presence: PresenceState,
  userId: number,
  status: UserStatus,
): void {
  if (status === "offline") {
    delete presence.map[userId];
  } else {
    presence.map[userId] = status;
  }
}

/** Wholesale replacement, never a merge — the list is sparse, and merging
 * leaves everybody who left while we were away lit forever. `enabled` is
 * deliberately NOT touched here: the capability is the state body's to set. */
export function applyPresenceSync(presence: PresenceState, rows: PresenceEntry[]): void {
  presence.map = {};
  for (const r of rows) {
    if (r.status !== "offline") presence.map[r.user_id] = r.status;
  }
}

/** From GET /v1/bootstrap: the key's presence IS the capability probe. */
export function applyPresenceState(presence: PresenceState, rows: PresenceEntry[] | null): void {
  presence.enabled = rows !== null;
  applyPresenceSync(presence, rows ?? []);
}

/** null for offline, unknown ids AND unnameable statuses — all three draw
 * nothing (a grey dot would assert "offline" about a newer state). */
export function statusOf(presence: PresenceState, userId: number): UserStatus | null {
  const s = presence.map[userId];
  if (s === undefined) return null;
  if (s !== "online" && s !== "afk" && s !== "dnd" && s !== "invisible" && s !== "in_call") {
    return null;
  }
  return s;
}

/** The channel to name in somebody's "In a call" status (D269), or null when
 * there is nothing to name.
 *
 * The server never says WHERE a call is — the status is one word — so the name
 * comes from the call rosters this client was already told about, which are
 * exactly the calls in channels it may see (`rooms::announce` addresses a
 * private channel's to its members, D114). A room we cannot see has no roster
 * here, and the status stays the bare "In a call": the mask working, not a
 * hole in it.
 *
 * A DM is never named — its name is a person's, and "In a call in «Olya»" is a
 * sentence about the wrong thing — so rooms are scanned for one that is not a
 * DM rather than the first match being rejected if it is one. */
export function callPlace(rooms: Room[], userId: number): string | null {
  const room = rooms.find((r) => !isDmRoom(r) && r.callMemberIds.includes(userId));
  return room && room.name !== "" ? room.name : null;
}

/** The word a status dot carries as its tooltip — one function for every
 * surface that draws one (the sidebar's rows, the profile card), so the four
 * of them cannot word one status differently. `place` is `callPlace`'s answer
 * and is read under "in_call" alone; every other status is one word with
 * nowhere to put it. */
export function presenceTitle(status: UserStatus, place: string | null, t: Dict): string {
  switch (status) {
    case "afk":
      return t.presenceAfk();
    case "dnd":
      return t.presenceDnd();
    case "invisible":
      return t.presenceInvisible();
    case "in_call":
      return place ? t.presenceInCallAt(place) : t.presenceInCall();
    default:
      return t.presenceOnline();
  }
}

/** Whose presence dot a row may draw (D194): DMs only, friends only, and the
 * call pill owns the corner when a call is live. */
export function dottedPeer(room: Room, friends: FriendsState): number | null {
  if (room.callMemberIds.length > 0) return null;
  if (!isDmRoom(room) || room.peerId === null) return null;
  return isFriend(friends, room.peerId) ? room.peerId : null;
}

// ---------------------------------------------------------------------------
// Calls (read-only, F3)

export function callsApply(calls: Record<number, Call>, call: Call): void {
  calls[call.channel_id] = call;
}

export function callsEnd(calls: Record<number, Call>, channelId: number): void {
  delete calls[channelId];
}

// ---------------------------------------------------------------------------
// Our own call (F4)

export function voiceConnecting(channelId: number): VoiceCall {
  return {
    channelId,
    phase: "connecting",
    company: false,
    muted: false,
    deafened: false,
    code: null,
    unlisted: 0,
    peers: [],
    speaking: [],
    rttMs: 0,
    lossPct: 0,
    concealmentPct: 0,
    volumes: {},
    share: null,
    audience: {},
  };
}

/** The question to ask about the call `voice` describes, read the moment it
 * ends and before it is forgotten (docs/call-rating.md D515): a call in which
 * somebody else was with us while it was up. A call nobody answered, one that
 * never came up and one we sat in alone have nothing to rate. */
export function ratingAsk(voice: VoiceCall | null): { channelId: number } | null {
  return voice?.company ? { channelId: voice.channelId } : null;
}

export function voicePeerUpsert(voice: VoiceCall, peer: VoicePeer): void {
  const i = voice.peers.findIndex((p) => p.userId === peer.userId);
  if (i >= 0) voice.peers[i] = peer;
  else voice.peers.push(peer);
}

export function voicePeerLeft(voice: VoiceCall, userId: number): void {
  voice.peers = voice.peers.filter((p) => p.userId !== userId);
  voice.speaking = voice.speaking.filter((id) => id !== userId);
}

export function voicePeerFlags(
  voice: VoiceCall,
  userId: number,
  muted: boolean,
  deafened: boolean,
): void {
  const p = voice.peers.find((x) => x.userId === userId);
  if (p) {
    p.muted = muted;
    p.deafened = deafened;
  }
}

export function voicePeerRtt(voice: VoiceCall, userId: number, rttMs: number): void {
  const p = voice.peers.find((x) => x.userId === userId);
  if (p) p.rttMs = rttMs;
}

export function voiceServerMute(voice: VoiceCall, userId: number, muted: boolean): void {
  const p = voice.peers.find((x) => x.userId === userId);
  if (p) p.serverMuted = muted;
}

export function voicePeerShare(
  voice: VoiceCall,
  userId: number,
  active: boolean,
  paused: boolean,
  hasAudio: boolean,
): void {
  const p = voice.peers.find((x) => x.userId === userId);
  if (p) {
    p.screenSharing = active;
    p.screenSharePaused = paused;
    p.screenShareHasAudio = hasAudio;
  }
  if (voice.share?.userId === userId) {
    if (!active) voice.share = null;
    else {
      voice.share.paused = paused;
      voice.share.hasAudio = hasAudio;
    }
  }
}

/** A peer's camera on or off (camera-plan.md D688) — only the mark's state
 * (D703): the browser subscribes to no camera. */
export function voicePeerCamera(voice: VoiceCall, userId: number, active: boolean): void {
  const p = voice.peers.find((x) => x.userId === userId);
  if (p) p.cameraOn = active;
}
